import * as ganttLib from 'dhtmlx-gantt';
// dhtmlx-gantt exports { gantt, default } — prefer named export
const gantt = ganttLib.gantt ?? ganttLib.default ?? ganttLib;
import { api } from '../api.js';
import { canDo } from '../auth.js';
import { computeSchedule } from '../utils/schedule.js';
import { formatDate, formatDateDisplay, formatTime, jsToIsoDow } from '../utils/date.js';
import { feverEmoji } from '../utils/format.js';
import { iconLock, iconRefresh, iconUndo } from '../utils/icons.js';
import { getTaskClass, bufferDays, isMilestoneOverdue } from '../utils/ccpm.js';
import { showNotification } from '../components/notification.js';
import { showContextMenu, hideContextMenu } from '../components/context-menu.js';
import { openModal } from '../components/modal.js';
import { createProjectMultiSelect } from '../components/multi-select.js';
import {
  mountTaskInspector,
  openTaskInspector,
  openCreateTaskInspector,
  closeTaskInspector,
  refreshInspectorTask,
  getInspectorDiff,
  getInspectorCreatePayload,
  showInspectorError,
} from '../components/task-inspector.js';
import {
  mountProjectInspector,
  openProjectInspector,
  openCreateProjectInspector,
  closeProjectInspector,
  refreshInspectorProject,
  getProjectInspectorDiff,
  getProjectInspectorCreatePayload,
  getProjectInspectorBuildPayload,
  showProjectInspectorError,
} from '../components/project-inspector.js';
import { loadGanttState, saveGanttState, defaultGanttState } from '../utils/storage.js';
import {
  dhtmlxLinkTypeToDepType,
  depTypeToDhtmlxLinkType,
  depTypeShortRu,
  RU_TO_DEP_TYPE,
} from '../utils/dep-types.js';

// Available columns. Order here defines display order in grid + context menu.
// `alwaysOn: true` forbids hiding (only `text` is mandatory — without it tree breaks).
const COLUMN_DEFS = [
  { name: 'id',           label: 'ID',           width: 60,  align: 'center',
    // Sub-plan C (Руслан 2026-05-10): для project-row показываем
    // project_code (ручной номер 2026/14, P-2026-007 и т.п.) если задан,
    // иначе системный `p_NN`. Для задач — обычный numeric id.
    template: (t) => {
      if (typeof t.id === 'string' && t.id.startsWith('p_')) {
        return t.project_code ?? t.id;
      }
      return t.id;
    } },
  { name: 'text',         label: 'Задача',       width: 220, tree: true, alwaysOn: true,
    editor: { type: 'text', map_to: 'text' },
    template: (t) => {
      // Roxber Teal (мокап line 281-292):
      // - Project/buffer — text как есть, без иконки.
      // - Milestone (веха) — diamond-иконка ◆ + italic имя.
      // - Phase — diamond-иконка приглушённого цвета.
      // - Tasks — status-точка + обычное имя.
      if (t.type === 'project' || t._isBuffer) return t.text;
      if (t._isPhase) {
        return `<span class="ico ico-diamond ico-phase" aria-hidden="true">◆</span>` +
               `<span class="task-name-text name-phase">${t.text}</span>`;
      }
      if (t._isMilestone) {
        return `<span class="ico ico-diamond" aria-hidden="true">◆</span>` +
               `<span class="task-name-text name-milestone">${t.text}</span>`;
      }
      const cls = `sdot sdot-${t._status || 'todo'}`;
      const nameCls = t._status === 'cancelled' ? 'task-name-text name-cancelled' : 'task-name-text';
      return `<span class="${cls}" aria-hidden="true"></span><span class="${nameCls}">${t.text}</span>`;
    } },
  // F33 (2026-05-10): Дни = Окончание − Начало (calendar days), не из
  // duration_days поля. Edit → PATCH planned_end_date (Начало фиксируется).
  { name: 'duration',     label: 'Дни',          width: 45,  align: 'center',
    template: (t) => computeDaysFromDates(t.plannedStart, t.plannedEnd),
    editor: { type: 'number', map_to: 'duration', min: 1, max: 999 } },
  // F33: Дни (Баз.) = Баз.Окончание − Баз.Начало. Edit → PATCH baseline_end_date.
  { name: 'baselineDuration', label: 'Дни (баз.)', width: 60, align: 'center',
    template: (t) => computeDaysFromDates(t.baselineStart, t.baselineEnd),
    editor: { type: 'number', map_to: 'baselineDuration', min: 1, max: 999 } },
  { name: 'resource',     label: 'Ресурс',       width: 75,  resize: true,
    editor: { type: 'select', map_to: 'owner_resource_id', options: [] } },
  { name: 'cc',           label: 'CC',           width: 30,  align: 'center',
    template: (t) => t.cc ? `<span class="cc-mark">${t.cc}</span>` : '' },
  { name: 'status',       label: 'Статус',       width: 80,  resize: true,
    editor: { type: 'select', map_to: 'status', options: [
      { key: 'todo',        label: 'todo' },
      { key: 'in_progress', label: 'in progress' },
      { key: 'done',        label: 'done' },
      { key: 'blocked',     label: 'blocked' },
      { key: 'cancelled',   label: 'cancelled' },
    ]}},
  { name: 'progress',     label: '%',            width: 50,  align: 'center',
    // Веха = точка фиксации, без % выполнения (Руслан 2026-05-10).
    // Читаем work_pct (0..100, смысловое поле), а не progress (0..1, служебное
    // DHTMLX-поле для прогресс-бара). Раньше через progress было рассинхронизация
    // editor/template: editor показывал 0.01 вместо 1, а сохранение интерпретировало
    // 1 как 100%. work_pct = одна шкала и в template, и в editor, и в save handler.
    template: (t) => t._isMilestone ? '' : (t.work_pct != null ? `${Math.round(Number(t.work_pct))}%` : ''),
    // type:'text' (не 'number') — спинер мешает быстрому вводу однозначных
    // процентов (см. parent_task_id и Минуты/Факт ниже). parseInt + clamp в onSave.
    editor: { type: 'text', map_to: 'work_pct' } },
  { name: 'minutes',      label: 'Минуты',       width: 60,  align: 'center',
    editor: { type: 'text', map_to: 'minutes' } },
  { name: 'faktic',       label: 'Факт',         width: 60,  align: 'center',
    editor: { type: 'text', map_to: 'faktic' } },
  { name: 'predecessors', label: 'Pred',         width: 70,  resize: true,
    editor: { type: 'text', map_to: 'predecessors' } },
  { name: 'successors',   label: 'Succ',         width: 70,  resize: true,
    editor: { type: 'text', map_to: 'successors' } },
  // F16 (2026-05-10): inline-колонка «Main» = parent_task_id. Вписал
  // цифру id → задача подчинилась (виртуальная сводная по Phase D).
  // Backend trigger validate_milestone_parent (миграция 034) разрешает
  // любую задачу как parent при условии 2-level depth.
  // type:'text' (не 'number') — спинер мешает вводить 3-значные id;
  // parseInt + валидация в onSave (см. ниже columnName === 'parent_task_id').
  { name: 'parent_task_id', label: 'Main', width: 70, align: 'center',
    template: (t) => t.parent_task_id ?? '',
    editor: { type: 'text', map_to: 'parent_task_id' } },
  // F30 (2026-05-10): inline-edit date-колонок с lock-state condition.
  // baseline_* колонки editable когда projects.baseline_locked === false
  // (замочек ОТКРЫТ — можно править baseline). planned_* колонки editable
  // когда baseline_locked === true (замочек ЗАКРЫТ — план двигается, baseline
  // фиксирован). Проверка в onSave handler через isDateColumnEditable().
  // Date editor type='date' рендерит native input[type=date] — формат YYYY-MM-DD.
  { name: 'baselineStart', label: 'Баз. начало',    width: 90, align: 'center',
    // Веха = точка фиксации, у неё нет «начала» (Руслан 2026-05-10).
    // Поле t.baselineStart остаётся ISO (для editor + computeDaysFromDates),
    // formatDateDisplay даёт display-only DD-MM-YY (Руслан 2026-05-10).
    template: (t) => t._isMilestone ? '' : formatDateDisplay(t.baselineStart),
    editor: { type: 'date', map_to: 'baselineStart' } },
  { name: 'baselineEnd',   label: 'Баз. окончание', width: 95, align: 'center',
    template: (t) => formatDateDisplay(
      t.baselineEnd || (t._isMilestone ? t.deadline : null)
    ),
    editor: { type: 'date', map_to: 'baselineEnd' } },
  { name: 'plannedStart',  label: 'Начало',         width: 80, align: 'center',
    template: (t) => t._isMilestone ? '' : formatDateDisplay(t.plannedStart),
    editor: { type: 'date', map_to: 'plannedStart' } },
  { name: 'plannedEnd',    label: 'Окончание',      width: 95, align: 'center',
    template: (t) => renderPlannedEndCell(t),
    editor: { type: 'date', map_to: 'plannedEnd' } },
  { name: 'notes',        label: 'Заметки',      width: 150,
    editor: { type: 'text', map_to: 'notes' } },
  // Sub-plan D (Руслан 2026-05-10): тип CCPM-расчёта (миграция 039).
  // NULL = legacy (поведение как fixed_work). При выборе типа — будущий
  // backend recalc будет учитывать (сейчас только хранение + UI).
  { name: 'calc_type',    label: 'Тип',          width: 130, align: 'center',
    template: (t) => CALC_TYPE_LABELS[t.calc_type] ?? '—',
    editor: { type: 'select', map_to: 'calc_type', options: [
      { key: '',                label: '—' },
      { key: 'fixed_duration',  label: 'Фикс длительность' },
      { key: 'fixed_work',      label: 'Фикс трудозатраты' },
      { key: 'fixed_scope',     label: 'Фикс объём' },
    ]}},
  // Sub-plan B (Руслан 2026-05-10): 6 колонок Объём + Затраты. Скрыты по
  // умолчанию (пользователь явно сказал «я скрою эти колонки» — служат
  // для cost-weighted % проекта). Объём = revenue (cost_rub × work_pct).
  // Затраты = cost (expense_planned_rub vs expense_actual_rub, миграция 036).
  { name: 'volume_total',     label: 'Объём общ',      width: 90,  align: 'right',
    template: (t) => fmtRub(t.cost_rub),
    // Inline-edit cost_rub (плановая сумма по контракту). Объём факт +
    // в работе пересчитываются автоматически из cost_rub × work_pct.
    // type='money' — кастомный editor (выше moneyEditor): inputmode='decimal'
    // (numeric клавиатура на мобиле) + suffix ₽ + parseMoney с поддержкой
    // «10 000», «10,5», «10к» (=10000), «1м» (=1_000_000).
    editor: { type: 'money', map_to: 'cost_rub' } },
  { name: 'volume_done',      label: 'Объём факт',     width: 90,  align: 'right',
    // Computed (cost_rub × work_pct/100) — readonly. Меняется через
    // редактирование «Объём общ» (cost_rub) или колонки «%» (work_pct).
    template: (t) => fmtRub(t.cost_rub != null
      ? Number(t.cost_rub) * (Number(t.work_pct ?? 0) / 100)
      : null) },
  { name: 'volume_remaining', label: 'Объём в работе', width: 100, align: 'right',
    // Computed (cost_rub − Объём факт) — readonly.
    template: (t) => fmtRub(t.cost_rub != null
      ? Number(t.cost_rub) * (1 - Number(t.work_pct ?? 0) / 100)
      : null) },
  { name: 'expense_total',     label: 'Затраты общ',    width: 90,  align: 'right',
    template: (t) => fmtRub(t.expense_planned_rub),
    editor: { type: 'money', map_to: 'expense_planned_rub' } },
  { name: 'expense_actual',    label: 'Затраты факт',   width: 90,  align: 'right',
    template: (t) => fmtRub(t.expense_actual_rub),
    editor: { type: 'money', map_to: 'expense_actual_rub' } },
  { name: 'expense_remaining', label: 'Затраты оставш', width: 100, align: 'right',
    template: (t) => {
      if (t.expense_planned_rub == null && t.expense_actual_rub == null) return '';
      const r = Number(t.expense_planned_rub ?? 0) - Number(t.expense_actual_rub ?? 0);
      const txt = fmtRub(r);
      return r < 0 ? `<span style="color:var(--fever-red)">${txt}</span>` : txt;
    } },
];

// Sub-plan D: метки типов CCPM-расчёта для template колонки «Тип».
// NULL → '—'. В гриде короткие, в инспекторе полные подписи.
const CALC_TYPE_LABELS = {
  fixed_duration: 'Фикс длительн.',
  fixed_work:     'Фикс труд.',
  fixed_scope:    'Фикс объём',
};

// Sub-plan B helper: форматирование рублей. NULL/undefined → ''.
// Округление до целых (мелочь не нужна), русская локаль (10 000 ₽).
function fmtRub(n) {
  if (n == null || n === '') return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return '';
  return Math.round(num).toLocaleString('ru-RU') + ' ₽';
}

// Парсер денежных строк → Number (или null если пусто).
// Принимает: «10000», «10 000», «10,5», «10.5», «10к» (=10000), «1М» (=1000000),
// «1500₽», «10 000 ₽». Локаль ru-RU: «,» — десятичный разделитель.
// Возвращает null для пустой строки, NaN для невалидного — caller проверит.
function parseMoney(raw) {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  let multiplier = 1;
  const m = s.match(/^(.+?)\s*([кkКK]|[мMМ])\s*₽?\s*$/);
  let body = s;
  if (m) {
    body = m[1];
    const suffix = m[2];
    multiplier = (suffix === 'м' || suffix === 'M' || suffix === 'М') ? 1_000_000 : 1_000;
  }
  const cleaned = body.replace(/[\s ₽]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  if (!Number.isFinite(n)) return NaN;
  return n * multiplier;
}

// Кастомный DHTMLX inline-editor type='money'. Регистрируется в configureGantt.
// Зачем: type='number' имплицитно cap'ает на 100 (наследие progress-bar);
// type='text' решает но даёт буквенную клавиатуру на мобиле. Money-editor
// = <input type="text" inputmode="decimal"> + suffix ₽ + parseMoney
// (поддержка пробелов / запятой / суффиксов «к»/«м»).
const moneyEditor = {
  show(_id, _column, _config, placeholder) {
    placeholder.innerHTML =
      `<div class="gantt-money-editor">` +
        `<input type="text" inputmode="decimal" autocomplete="off" data-1p-ignore>` +
        `<span class="gantt-money-suffix">₽</span>` +
      `</div>`;
  },
  hide() { /* DHTMLX чистит placeholder сам */ },
  set_value(value, _id, _column, node) {
    const input = node.querySelector('input');
    if (!input) return;
    input.value = (value == null || value === '')
      ? ''
      : Math.round(Number(value)).toLocaleString('ru-RU');
    input.select();
  },
  get_value(_id, _column, node) {
    return node.querySelector('input')?.value ?? '';
  },
  is_valid(value) {
    if (!value || String(value).trim() === '') return true;
    const n = parseMoney(value);
    return n === null || (Number.isFinite(n) && n >= 0);
  },
  // Обязательный метод DHTMLX 8.x — без него controller.js:288 кидает
  // «is_changed is not a function» при keydown/save. Сравниваем парсенные
  // значения, чтобы «10 000» и «10000» считались эквивалентными.
  is_changed(value, _id, _column, node) {
    const original = (value == null || value === '') ? null : Number(value);
    const current = parseMoney(node.querySelector('input')?.value ?? '');
    if (original === null && current === null) return false;
    if (Number.isNaN(current)) return true;  // мусор → save → handler выдаст toast
    return original !== current;
  },
  // DHTMLX вызывает save() для своей внутренней логики; мы используем
  // attachEvent('onSave') в onTaskInlineSave handler, поэтому здесь noop.
  // Возвращаем true — «успешно сохранили» (DHTMLX считает что мы записали).
  save() { return true; },
  focus(node) {
    node.querySelector('input')?.focus();
  },
};

// F33 (2026-05-10): Дни в колонке = разница в календарных днях между
// startStr и endStr. Возвращает '' если хотя бы одна дата пуста или невалидна.
// inclusive — день старта и день окончания оба считаются (3-5 мая = 3 дня).
function computeDaysFromDates(startStr, endStr) {
  if (!startStr || !endStr) return '';
  const s = new Date(startStr);
  const e = new Date(endStr);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return '';
  const days = Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
  return days;
}

// Окончание расчётной даты + просрочка относительно baseline. Только просрочка
// красным; опережение не показываем (по CCPM-философии — нерелевантно, см.
// «Решения по открытым вопросам» в плане).
function renderPlannedEndCell(t) {
  // F11 fix (2026-05-10): для milestone fallback на t.deadline (нет отдельных
  // start/end полей в БД).
  let planned = t.plannedEnd;
  let baseline = t.baselineEnd;
  if (!planned && t._isMilestone && t.deadline) planned = t.deadline;
  if (!baseline && t._isMilestone && t.deadline) baseline = t.deadline;
  if (!planned) return '';
  // Расчёт дней опережает форматирование: оба поля приходят ISO,
  // new Date(...) парсит их корректно. На display уже даём DD-MM-YY.
  const plannedDisplay = formatDateDisplay(planned);
  if (!baseline) return plannedDisplay;
  const days = Math.round((new Date(planned) - new Date(baseline)) / 86400000);
  if (days > 0) {
    return `${plannedDisplay} <span style="color:var(--fever-red, #f85149);font-weight:500;">+${days}д</span>`;
  }
  return plannedDisplay;
}

const STATUS_RU = {
  todo:        'todo',
  in_progress: 'in progress',
  done:        'done',
  cancelled:   'cancelled',
  blocked:     'blocked',
};

let _recurringDows  = new Set(); // ISO days-of-week that have recurring blocks
let _dragBackup     = null;       // snapshot of task state before drag, for rollback
let _savingEl       = null;       // toolbar saving indicator
let _allProjects    = [];         // full list (any status) for the multi-select
let _allPortfolios  = [];         // portfolio defs for grouping in multi-select (Wave 2)
let _allTasks       = [];         // full unfiltered tasks for re-render
let _allDeps        = [];         // full unfiltered deps
let _resourcesCache = [];         // resources for re-render
let _selectedIds    = new Set();  // currently visible project ids
let _selectedTaskId = null;       // task id currently shown in inspector (Phase 1)
let _selectedProjectId = null;    // project id currently shown in project-inspector
let _insertHotkeyListener = null; // bound document keydown listener for `Insert` (Phase 4)
let _pollIntervalId = null;       // background refetch tick (auto-refresh от бота)
let _taskFetchParams = {};        // params used for the initial tasks() — replayed by polling
let _visibilityListenerAttached = false; // single-shot visibilitychange listener flag
let _filters = {
  fever:        new Set(['green', 'yellow', 'red']),
  statusActive: true,    // active+queued+paused
  statusDone:   false,   // done+cancelled
  ccOnly:       false,
};
let _visibleColumns = new Set(['id', 'text', 'duration', 'resource', 'cc']);
let _currentScale   = 'week';        // last user-picked scale, persisted to localStorage
let _columnWidths   = {};            // user-resized widths, persisted to localStorage
let _gridWidth      = 420;           // ширина таблицы (splitter), persisted to localStorage
let _showCurrentInBaselineMode = true;  // Чекбокс «Current» в режиме правки эталона. ON=показывать призраком, OFF=полностью скрыть.
let _refreshBaselineButtons = () => {}; // set up in initGantt; called when selection changes

// Поля, на которые backend update_task запускает CPM forward-pass (FS/SS/FF + lag):
// зеркалит PLANNED_RECALC_FIELDS + BASELINE_RECALC_FIELDS + STRUCT_RECALC_FIELDS
// из backend/app/api/tasks.py. Когда меняем такое поле через PATCH, backend
// сдвигает связанные задачи — фронту нужен полный рефетч grid, иначе UI
// показывает старые даты потомков.
const RECALC_TRIGGER_FIELDS = new Set([
  'planned_start_date', 'planned_end_date', 'duration_days',
  'calc_type', 'ruslan_minutes',
  'baseline_start_date', 'baseline_end_date',
  'baseline_duration_days', 'baseline_ruslan_minutes',
  'parent_task_id',
]);

async function patchTaskAndRefetchIfRecalc(taskId, body) {
  const updated = await api.patchTask(taskId, body);
  const needsRefetch = Object.keys(body).some(k => RECALC_TRIGGER_FIELDS.has(k));
  if (needsRefetch) {
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
  }
  return updated;
}

function persistState() {
  saveGanttState({
    selectedIds:    [..._selectedIds],
    fever:          [..._filters.fever],
    statusActive:   _filters.statusActive,
    statusDone:     _filters.statusDone,
    ccOnly:         _filters.ccOnly,
    visibleColumns: [..._visibleColumns],
    scale:          _currentScale,
    columnWidths:   { ..._columnWidths },
    gridWidth:      _gridWidth,
    showCurrentInBaselineMode: _showCurrentInBaselineMode,
  });
  _refreshBaselineButtons();
}

// ── Public entry point ──────────────────────────────────────────────────────

export async function initGantt(container, projectId = null) {
  container.style.cssText = 'height:100%; display:flex; flex-direction:column; overflow:hidden; position:relative;';
  // Reset selection on (re)entry so a stale id from a previous /gantt visit
  // does not highlight an unrelated row.
  _selectedTaskId = null;
  _selectedProjectId = null;

  // Toolbar
  const toolbar = buildToolbar(projectId);
  container.appendChild(toolbar);

  // Gantt host div
  const ganttDiv = document.createElement('div');
  ganttDiv.id    = 'ccpm-gantt-host';
  ganttDiv.style.cssText = 'flex:1; min-height:0; overflow:hidden;';
  container.appendChild(ganttDiv);

  // Roxber Teal: legend footer (мокап line 689-711) — компактная полоса
  // 38px со swatches для каждого состояния bar'а + Связь / Сегодня.
  const legend = document.createElement('div');
  legend.className = 'ccpm-legend';
  legend.innerHTML = `
    <span class="lg-group"><span class="lg-swatch lg-baseline"></span>Baseline</span>
    <span class="lg-divider"></span>
    <span class="lg-group"><span class="lg-swatch lg-todo"></span>Todo</span>
    <span class="lg-group"><span class="lg-swatch lg-in-progress"></span>В работе</span>
    <span class="lg-group"><span class="lg-swatch lg-done"></span>Выполнено</span>
    <span class="lg-group"><span class="lg-swatch lg-blocked"></span>Заблокировано</span>
    <span class="lg-group"><span class="lg-swatch lg-cancelled"></span>Отменено</span>
    <span class="lg-divider"></span>
    <span class="lg-group"><span class="lg-swatch lg-cc"></span>Critical Chain</span>
    <span class="lg-group"><span class="lg-swatch lg-buffer"></span>Buffer</span>
    <span class="lg-group"><span class="lg-diamond"></span>Веха</span>
    <span class="lg-divider"></span>
    <span class="lg-group"><span class="lg-dep-mark"></span>Связь · КН/НН/КК/НК</span>
    <span class="lg-group"><span class="lg-today"></span>Сегодня</span>
  `;
  container.appendChild(legend);

  // Show loading while fetching
  ganttDiv.innerHTML = '<div class="loading-spinner">Загрузка данных...</div>';

  try {
    const taskParams = projectId ? { project_id: projectId } : {};
    const depParams  = projectId ? { project_id: projectId } : {};
    _taskFetchParams = taskParams; // replayed by polling

    // Fetch ALL projects (any status) so the multi-select can show backlog/done/cancelled too.
    // Visibility in the chart is controlled by _selectedIds.
    const [allProjects, allTasks, deps, resources, recurringResp, portfolios] = await Promise.all([
      api.projects(),
      api.tasks(taskParams),
      api.dependencies(depParams),
      api.resources(),
      api.recurringBlocks().catch(() => ({ blocks: [] })),
      api.portfolios().catch(() => []),
    ]);

    _allProjects    = allProjects;
    _allTasks       = allTasks;
    _allDeps        = deps;
    _resourcesCache = resources;
    _allPortfolios  = portfolios;

    // Restore state from localStorage (only on multi-project view).
    const saved = projectId ? null : (loadGanttState() ?? defaultGanttState());

    if (projectId) {
      _selectedIds = new Set([Number(projectId)]);
    } else if (saved.selectedIds && saved.selectedIds.length) {
      // Validate: keep only ids that still exist among projects.
      const valid = new Set(allProjects.map(p => p.id));
      _selectedIds = new Set(saved.selectedIds.filter(id => valid.has(id)));
      if (!_selectedIds.size) {
        _selectedIds = new Set(
          allProjects
            .filter(p => ['active', 'queued', 'paused'].includes(p.status))
            .map(p => p.id)
        );
      }
    } else {
      _selectedIds = new Set(
        allProjects
          .filter(p => ['active', 'queued', 'paused'].includes(p.status))
          .map(p => p.id)
      );
    }

    if (saved) {
      _filters.fever        = new Set(saved.fever ?? ['green', 'yellow', 'red']);
      _filters.statusActive = !!saved.statusActive;
      _filters.statusDone   = !!saved.statusDone;
      _filters.ccOnly       = !!saved.ccOnly;
      _visibleColumns       = new Set(saved.visibleColumns ?? ['id', 'text', 'duration', 'resource', 'cc']);
      // Always keep the mandatory column.
      _visibleColumns.add('text');
      _currentScale         = saved.scale ?? 'week';
      _columnWidths         = { ...(saved.columnWidths ?? {}) };
      _gridWidth            = typeof saved.gridWidth === 'number' ? saved.gridWidth : 420;
      _showCurrentInBaselineMode = saved.showCurrentInBaselineMode !== false;
      // Apply persisted widths to COLUMN_DEFS so the first render uses them.
      for (const col of COLUMN_DEFS) {
        if (typeof _columnWidths[col.name] === 'number') col.width = _columnWidths[col.name];
      }
    }

    // Build recurring blocks lookup (ISO dow set)
    const blocks = recurringResp.blocks ?? [];
    _recurringDows = new Set(blocks.flatMap(b => b.days_of_week ?? []));

    // Init Gantt with current selection
    ganttDiv.innerHTML = '';
    gantt.plugins({ marker: true, tooltip: true });
    configureGantt();
    gantt.init(ganttDiv);
    // DHTMLX 8.x GPL не имеет marker plugin — gantt.addMarker молчаливо
    // не работает. Рисуем TODAY pill + вертикальную линию через
    // самодельный DOM-overlay в `.gantt_task` (timeline-сторона). Слой
    // обновляется на каждый rerender чтобы попадать на правильный X
    // при смене масштаба и скролле.
    gantt.attachEvent('onGanttRender', () => {
      attachCustomColumnResize();
      attachCustomGridSplitter();
      installTodayOverlay();
      installDepLabels();
      installProjectBrackets();
      installZoomOverlay();
    });
    // F18 fix (2026-05-10): подписи КН/НН/КК на стрелках при vertical scroll
    // отвязывались от линий — координаты вычислены через getBoundingClientRect
    // на момент последнего render, а после scroll стрелки сдвинулись. Re-attach
    // labels на каждый scroll-event (DHTMLX вызывает onGanttScroll при wheel /
    // drag scrollbar / keyboard). Дёшево: installDepLabels() очищает старые
    // overlays и пересчитывает позиции из актуальных DOM.
    // F28 (2026-05-10): то же для бар-выравнивания — без re-align на scroll
    // бары «разбегаются» от своих строк (DHTMLX переставляет inline top,
    // delta-метод срабатывает заново на каждый scroll-кадр).
    gantt.attachEvent('onGanttScroll', () => {
      installProjectBrackets();
      installDepLabels();
    });
    // 2026-05-12: drag splitter'а живёт в attachCustomGridSplitter() — DHTMLX
    // GPL не рендерит {resizer:true} в layout и не зовёт onGridResizeEnd.
    // Ширина grid'а сохраняется в _gridWidth + persistState() из mouseup
    // нашего собственного splitter'а.
    // MS-Project-style baseline-полоска под актуальным баром. Pulls
    // first_baseline_* from task model (LEFT JOIN baseline_history в backend).
    // Toggle через [data-show-baseline] checkbox; persisted in localStorage.
    installBaselineLayer();
    installBaselineLinksLayer();
    installMilestoneDateLayer();
    installSummaryLabelsLayer();
    // M4: трекинг open/close сводных в localStorage. attachEvent — однократно.
    if (!_closedStateAttached) {
      _closedStateAttached = true;
      gantt.attachEvent('onTaskOpened', (id) => {
        const set = getClosedTaskIds();
        set.delete(String(id));
        setClosedTaskIds(set);
      });
      gantt.attachEvent('onTaskClosed', (id) => {
        const set = getClosedTaskIds();
        set.add(String(id));
        setClosedTaskIds(set);
      });
    }
    rerenderGantt();

    // Mount inspector drawer inside the gantt container (Phase 1 — read-only view + actions).
    mountTaskInspector(container);
    mountProjectInspector(container);

    // Wire interactivity (Phase B+ — drag/drop, resize, → backend)
    _savingEl = toolbar.querySelector('.saving-indicator');
    wireInteractivity();

    // Mount project multi-select (skip when single-project view)
    if (!projectId) {
      const slot = toolbar.querySelector('.gantt-filters-slot');
      slot.appendChild(createProjectMultiSelect({
        items:       _allProjects,
        selectedIds: _selectedIds,
        portfolios:  _allPortfolios,
        onChange:    (newSet) => { _selectedIds = newSet; rerenderGantt(); persistState(); },
      }));
    }

    // Pre-fill filter checkboxes from restored state
    toolbar.querySelectorAll('input[data-fever]').forEach(cb => {
      cb.checked = _filters.fever.has(cb.dataset.fever);
    });
    const stActive = toolbar.querySelector('input[data-status="active"]');
    if (stActive) stActive.checked = _filters.statusActive;
    const stDone   = toolbar.querySelector('input[data-status="done"]');
    if (stDone)   stDone.checked   = _filters.statusDone;
    const ccPre    = toolbar.querySelector('input[data-cc-only]');
    if (ccPre)    ccPre.checked    = _filters.ccOnly;

    // Right-click on grid header → column visibility menu
    if (!projectId) {
      ganttDiv.addEventListener('contextmenu', (e) => {
        const headerCell = e.target.closest('.gantt_grid_head_cell');
        if (!headerCell) return;
        e.preventDefault();
        showColumnContextMenu(e.clientX, e.clientY);
      });
    }

    // Wire scale buttons
    toolbar.querySelectorAll('.scale-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        toolbar.querySelectorAll('.scale-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        _currentScale = btn.dataset.scale;
        setGanttScale(_currentScale);
        persistState();
      });
    });

    // Restore last-used scale from persisted state (default 'week' is already applied
    // by configureGantt; only re-apply if the saved choice differs).
    if (_currentScale !== 'week') {
      toolbar.querySelectorAll('.scale-btn').forEach(b => b.classList.remove('active'));
      const savedBtn = toolbar.querySelector(`.scale-btn[data-scale="${_currentScale}"]`);
      if (savedBtn) savedBtn.classList.add('active');
      setGanttScale(_currentScale);
    }

    // Wire "Show Baseline" checkbox — toggle для пунктирной полоски (Wave 7).
    // Состояние хранится в localStorage; layer перечитывает флаг на render.
    const baselineCb = toolbar.querySelector('[data-show-baseline]');
    if (baselineCb) {
      baselineCb.checked = isBaselineVisible();
      baselineCb.addEventListener('change', (e) => setBaselineVisible(e.target.checked));
    }

    // Wire "+ Новая задача" button + Insert hotkey (Phase 4).
    const newBtn = toolbar.querySelector('[data-new-task]');
    if (newBtn) newBtn.addEventListener('click', openNewTaskInspector);

    // Wire "+ Новый проект" — открывает project-инспектор в create-режиме.
    // Wave 4: видимость через canDo('project.create') (data-driven matrix).
    const newProjectBtn = toolbar.querySelector('[data-new-project]');
    if (newProjectBtn) {
      if (canDo('project.create')) {
        newProjectBtn.addEventListener('click', openNewProjectInspector);
      } else {
        newProjectBtn.hidden = true;
      }
    }

    // Старая кнопка `⇪ Импорт` удалена в Phase A (план 2026-05-09).
    // Импорт docx теперь встроен в модалку «Новый проект» (5 слотов 📁 +
    // переключатель Бизнес/Личный + кнопка «📊 Сформировать Диаграмму Гантта»).
    // См. handleBuildGanttFromDocs() ниже.

    // Wire baseline-группа (segment-pill из 3 значков: 🔓/🔒/🔄). Видимость
    // и target-projectId — динамические:
    //   — URL `/gantt/<id>` (projectId передан) → всегда видны, target=projectId.
    //   — URL `/gantt` (multi) → видны если выбран ровно один проект.
    // refreshBaselineButtons() вызывается из persistState/onChange selectedIds
    // и подсвечивает текущее состояние замка (locked/unlocked) у видимых задач.
    const baseGroup = toolbar.querySelector('[data-baseline-group]');
    const currentBaselineTarget = () =>
      projectId ?? (_selectedIds.size === 1 ? [..._selectedIds][0] : null);
    // Wave 4: baseline через matrix (default: owner/admin/manager — все три действия).
    const canBaseline = canDo('baseline.lock') || canDo('baseline.unlock') || canDo('baseline.rebaseline');
    const refreshBaselineButtons = () => {
      if (!baseGroup) return;
      const t = currentBaselineTarget();
      baseGroup.hidden = (t == null) || !canBaseline;
      if (baseGroup.hidden) return;
      // F12 (2026-05-10): подсветка lock-toggle по project.baseline_locked.
      const proj = _allProjects.find(x => x.id === t);
      const lockBtn = baseGroup.querySelector('.baseline-lock-toggle');
      if (lockBtn && proj) {
        const locked = !!proj.baseline_locked;
        lockBtn.innerHTML = iconLock(!locked, 14);
        lockBtn.classList.toggle('is-locked', locked);
        lockBtn.classList.toggle('is-unlocked', !locked);
        // Phase 2: data-edit-mode на baseline-group + .gantt_container
        // (CSS [data-edit-mode="baseline"] делает swap визуала и показывает
        // бейдж/чекбокс «Current»).
        const ganttRootEl = document.querySelector('.gantt_container');
        if (!locked) {
          baseGroup.dataset.editMode = 'baseline';
          if (ganttRootEl) {
            ganttRootEl.dataset.editMode = 'baseline';
            // OFF = current скрыт полностью; ON = current виден как skeleton.
            if (!_showCurrentInBaselineMode) ganttRootEl.dataset.currentHidden = 'true';
            else delete ganttRootEl.dataset.currentHidden;
          }
        } else {
          delete baseGroup.dataset.editMode;
          if (ganttRootEl) {
            delete ganttRootEl.dataset.editMode;
            delete ganttRootEl.dataset.currentHidden;
          }
        }
        // Восстановить состояние чекбокса
        const currentCb = baseGroup.querySelector('input[data-current-toggle]');
        if (currentCb) currentCb.checked = _showCurrentInBaselineMode;
      }
    };
    if (baseGroup) {
      baseGroup.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-baseline-action]');
        if (!btn) return;
        const t = currentBaselineTarget();
        if (t == null) return;
        const action = btn.dataset.baselineAction;
        // F12 (2026-05-10): убраны старые lock/unlock/rebaseline (наследие
        // Phase 4 per-task baseline). Оставлены 3 кнопки:
        // lock-toggle (project замочек), recalc-baseline, recalc-planned.
        if      (action === 'lock-toggle')        onBaselineLockToggle(t);
        else if (action === 'recalc-baseline')    onRecalcBaselineClick(t);
        else if (action === 'recalc-planned')     onRecalcPlannedClick(t);
        else if (action === 'reset-to-baseline')  onResetToBaselineClick(t);
      });
      // Чекбокс «Current» при unlock: ON = current виден призраком,
      // OFF = current полностью скрыт через [data-current-hidden] CSS.
      // State в localStorage.
      const currentCb = baseGroup.querySelector('input[data-current-toggle]');
      if (currentCb) {
        currentCb.addEventListener('change', () => {
          _showCurrentInBaselineMode = currentCb.checked;
          const ganttRootEl = document.querySelector('.gantt_container');
          if (ganttRootEl) {
            if (!_showCurrentInBaselineMode) ganttRootEl.dataset.currentHidden = 'true';
            else delete ganttRootEl.dataset.currentHidden;
          }
          persistState();
        });
      }
    }
    refreshBaselineButtons();
    // Сделаем функцию доступной из persistState — при изменении выбора в
    // multi-select project filter кнопки скроются/появятся автоматически.
    _refreshBaselineButtons = refreshBaselineButtons;

    if (_insertHotkeyListener) {
      document.removeEventListener('keydown', _insertHotkeyListener);
    }
    _insertHotkeyListener = (e) => {
      if (e.key !== 'Insert') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // Only act on /gantt — router-induced unmount might leave this listener if
      // navigation skipped initGantt's reattach path.
      if (!location.pathname.startsWith('/gantt')) return;
      e.preventDefault();
      openNewTaskInspector();
    };
    document.addEventListener('keydown', _insertHotkeyListener);

    // Wire fever / status / CC-only checkboxes
    toolbar.querySelectorAll('input[data-fever]').forEach(cb => {
      cb.addEventListener('change', () => {
        if (cb.checked) _filters.fever.add(cb.dataset.fever);
        else            _filters.fever.delete(cb.dataset.fever);
        rerenderGantt();
        persistState();
      });
    });
    toolbar.querySelectorAll('input[data-status]').forEach(cb => {
      cb.addEventListener('change', () => {
        const key = cb.dataset.status === 'active' ? 'statusActive' : 'statusDone';
        _filters[key] = cb.checked;
        rerenderGantt();
        persistState();
      });
    });
    const ccCheckbox = toolbar.querySelector('input[data-cc-only]');
    if (ccCheckbox) {
      ccCheckbox.addEventListener('change', () => {
        _filters.ccOnly = ccCheckbox.checked;
        rerenderGantt();
        persistState();
      });
    }

    // Background auto-refresh from the bot / other sessions.
    attachVisibilityListenerOnce();
    startPolling();

  } catch (e) {
    if (e.message === '401') return;
    ganttDiv.innerHTML = `<div class="loading-spinner" style="color:var(--text-danger)">
      Ошибка загрузки Gantt: ${e.message}
    </div>`;
    console.error('Gantt init error:', e);
  }
}

// ── Toolbar ─────────────────────────────────────────────────────────────────

function buildToolbar(projectId) {
  const div = document.createElement('div');
  div.className = 'gantt-toolbar';

  const scales = [
    { key: 'hour',    label: 'Час' },
    { key: 'day',     label: 'День' },
    { key: 'week',    label: 'Неделя' },
    { key: 'month',   label: 'Месяц' },
    { key: 'quarter', label: 'Квартал' },
  ];

  div.innerHTML = `
    <div class="gantt-toolbar-row">
      <span class="toolbar-label">Масштаб:</span>
      <div class="scale-controls">
        ${scales.map(s => `
          <button class="scale-btn${s.key === 'week' ? ' active' : ''}"
                  data-scale="${s.key}">${s.label}</button>
        `).join('')}
      </div>
      <button type="button" class="toolbar-btn-primary" data-new-task title="Новая задача (Ins)">+ Новая задача</button>
      <button type="button" class="toolbar-btn" data-new-project title="Создать новый проект (с опциональным импортом docx)">+ Новый проект</button>
      <span class="baseline-group" data-baseline-group hidden role="group" aria-label="Baseline">
        <span class="baseline-label">Baseline:</span>
        <button type="button" class="baseline-btn baseline-btn-svg baseline-lock-toggle" data-baseline-action="lock-toggle"
                title="Замочек baseline. Закрыт = baseline read-only. Открыт = можно править baseline-даты + вызывать «Пересчитать baseline».">${iconLock(false, 14)}</button>
        <button type="button" class="baseline-btn baseline-btn-svg" data-baseline-action="recalc-baseline"
                title="Пересчитать baseline по CPM (требует открытый замочек). Перезаписывает baseline_*_date всех задач проекта.">${iconRefresh(14)}<span class="baseline-btn-suffix">б</span></button>
        <button type="button" class="baseline-btn baseline-btn-svg" data-baseline-action="recalc-planned"
                title="Пересчитать текущий план по CPM (всегда доступно). Перезаписывает planned_*_date всех задач проекта.">${iconRefresh(14)}<span class="baseline-btn-suffix">п</span></button>
        <button type="button" class="baseline-btn baseline-btn-svg" data-baseline-action="reset-to-baseline"
                title="Привести план к базовому: planned_*_date := baseline_*_date для ВСЕХ задач (вкл. done/in_progress). Длительность и минуты тоже переписываются. Бюджет/расходы/факт — нет.">${iconUndo(14)}</button>
        <span class="baseline-mode-badge" title="Замочек открыт — Gantt в режиме правки эталона. Current показан призраком за baseline.">ЭТАЛОН</span>
        <label class="baseline-current-toggle" title="Current-план: ON — показан призраком, OFF — полностью скрыт (виден только baseline)">
          <input type="checkbox" data-current-toggle>
          <span>Current</span>
        </label>
      </span>
      <label class="filter-check baseline-checkbox-label" title="Показать пунктирную полоску baseline (первый план) под актуальным баром, как в MS Project">
        <input type="checkbox" data-show-baseline>
        <span class="baseline-toggle-label">Baseline</span>
      </label>
      <span class="saving-indicator" hidden>Сохранение…</span>
      <div class="gantt-filters-slot" style="margin-left:auto; display:flex; gap:var(--space-2);"></div>
      ${projectId ? `
        <span class="toolbar-label" style="color: var(--text-secondary);">
          Проект #${projectId}
        </span>
      ` : ''}
    </div>
    ${projectId ? '' : `
      <div class="gantt-toolbar-row gantt-toolbar-row-2">
        <span class="toolbar-label">Fever:</span>
        <label class="filter-check"><input type="checkbox" data-fever="green"  checked>🟢</label>
        <label class="filter-check"><input type="checkbox" data-fever="yellow" checked>🟡</label>
        <label class="filter-check"><input type="checkbox" data-fever="red"    checked>🔴</label>
        <span class="toolbar-divider"></span>
        <span class="toolbar-label">Статус:</span>
        <label class="filter-check"><input type="checkbox" data-status="active" checked>Active</label>
        <label class="filter-check"><input type="checkbox" data-status="done">Done</label>
        <span class="toolbar-divider"></span>
        <label class="filter-check"><input type="checkbox" data-cc-only>Только critical chain</label>
      </div>
    `}
  `;
  return div;
}

// Apply current _visibleColumns to gantt.config.columns and (optionally) re-render.
function applyColumnVisibility() {
  // Refresh resource dropdown options from current cache (may have grown
  // since module load) before handing columns to DHTMLX.
  const resourceCol = COLUMN_DEFS.find(c => c.name === 'resource');
  if (resourceCol?.editor) {
    resourceCol.editor.options = _resourcesCache.map(r => ({ key: String(r.id), label: r.name }));
  }

  const cols = COLUMN_DEFS
    .filter(c => _visibleColumns.has(c.name) || c.alwaysOn)
    // Strip our internal flag before handing to DHTMLX.
    .map(({ alwaysOn, ...rest }) => rest); // eslint-disable-line no-unused-vars

  // 2026-05-11: НЕ пересчитываем gantt.config.grid_width по сумме колонок —
  // иначе ручной drag splitter'а отбрасывается обратно на каждом render.
  // Ширина grid теперь живёт независимо в _gridWidth (persist в localStorage)
  // и меняется только через onGridResizeEnd. Лишние столбцы прячутся за
  // собственным gridScroll'ом (см. gantt.config.layout).
  gantt.config.columns    = cols;
}

/**
 * Custom column resize — dhtmlx-gantt 8.x GPL не имеет встроенного
 * column resize (PRO-only). Добавляем 8px hot-zone к правому краю каждого
 * `.gantt_grid_head_cell`, mousedown стартует drag, mousemove обновляет
 * width в COLUMN_DEFS + applyColumnVisibility() + gantt.render().
 * Ширины persist в localStorage через persistState().
 *
 * Вызывается после каждого `gantt.render()` через `onGanttRender`-событие,
 * т.к. DHTMLX пересоздаёт DOM при render и наши handle-элементы исчезают.
 */
function attachCustomColumnResize() {
  const cells = document.querySelectorAll('.gantt_grid_scale .gantt_grid_head_cell');
  cells.forEach((cell, idx) => {
    if (cell.querySelector('.ccpm-col-resize-handle')) return; // already attached
    const handle = document.createElement('div');
    handle.className = 'ccpm-col-resize-handle';
    cell.style.position = 'relative';
    cell.appendChild(handle);

    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const colDef = gantt.config.columns[idx];
      if (!colDef) return;
      const persistedDef = COLUMN_DEFS.find(c => c.name === colDef.name);
      if (!persistedDef) return;
      const startX     = e.clientX;
      const startWidth = persistedDef.width || 80;
      let pendingFrame = 0;

      const onMove = (ev) => {
        const newWidth = Math.max(20, startWidth + (ev.clientX - startX));
        persistedDef.width = newWidth;
        if (pendingFrame) return;
        pendingFrame = requestAnimationFrame(() => {
          applyColumnVisibility();
          gantt.render();
          pendingFrame = 0;
        });
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        if (pendingFrame) cancelAnimationFrame(pendingFrame);
        applyColumnVisibility();
        gantt.render();
        _columnWidths[persistedDef.name] = persistedDef.width;
        persistState();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  });
}

/**
 * Custom grid splitter — dhtmlx-gantt 8.x GPL не рендерит `{resizer: true}` в
 * layout (resizer_cell.gpl.js → module.exports = null; commercial-only feature).
 * Поэтому drag границы grid|timeline делаем своим div'ом: position: absolute
 * на правом крае `.gantt_grid`, mousedown стартует drag, mousemove меняет
 * gantt.config.grid_width + _gridWidth + rAF-throttled gantt.render(),
 * mouseup сохраняет ширину в localStorage через persistState().
 *
 * Splitter живёт sibling'ом `.gantt_layout` внутри gantt.$container, поэтому
 * переживает gantt.render() (DHTMLX перерисовывает только содержимое layout).
 * На каждый onGanttRender перепозиционируем — координаты grid'а после render
 * могут измениться (особенно сразу после нашего же drag'а).
 */
function attachCustomGridSplitter() {
  const container = gantt.$container;
  const grid      = container?.querySelector('.gantt_grid');
  if (!container || !grid) return;

  let splitter = container.querySelector('.ccpm-grid-splitter');
  if (!splitter) {
    splitter = document.createElement('div');
    splitter.className = 'ccpm-grid-splitter';
    container.appendChild(splitter);

    splitter.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Менять надо сам литерал в gantt.config.layout.cols (а не $config
      // у view-объекта): gantt.resetLayout() делает dropLayout +
      // rebuildLayout, который пересоздаёт view'ы из gantt.config.layout
      // и затирает любую запись в $config view'а текущего слоя.
      const gridLayoutDef = (gantt.config.layout?.cols || [])
        .find(c => c.id === 'gridArea');
      if (!gridLayoutDef) return;

      const startX     = e.clientX;
      const startWidth = gridLayoutDef.width || _gridWidth || 420;
      let pendingFrame = 0;
      document.body.style.cursor     = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (ev) => {
        const newWidth = Math.max(1, startWidth + (ev.clientX - startX));
        gridLayoutDef.width = newWidth;
        _gridWidth = newWidth;
        if (pendingFrame) return;
        pendingFrame = requestAnimationFrame(() => {
          gantt.resetLayout();
          reinstallTaskLayers();
          pendingFrame = 0;
        });
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor     = '';
        document.body.style.userSelect = '';
        if (pendingFrame) cancelAnimationFrame(pendingFrame);
        gantt.resetLayout();
        reinstallTaskLayers();
        persistState();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
  }

  // Position relative to container.
  const gRect = grid.getBoundingClientRect();
  const cRect = container.getBoundingClientRect();
  splitter.style.left   = (gRect.right - cRect.left - 3) + 'px';
  splitter.style.top    = (gRect.top   - cRect.top)      + 'px';
  splitter.style.height = gRect.height + 'px';
}

function showColumnContextMenu(x, y) {
  // F23 (2026-05-10): keepOpen + getLabel — меню не закрывается после клика
  // по чекбоксу, можно подряд показать/скрыть несколько колонок. Закрытие
  // только по outside-click / Esc / scroll (document handlers в context-menu.js).
  const isVisible = (c) => _visibleColumns.has(c.name) || c.alwaysOn;
  const labelFor  = (c) =>
    `${isVisible(c) ? '☑' : '☐'} ${c.label}${c.alwaysOn ? ' (обязательная)' : ''}`;
  const items = COLUMN_DEFS.map(c => ({
    label:    labelFor(c),
    getLabel: () => labelFor(c),
    disabled: c.alwaysOn,
    keepOpen: !c.alwaysOn,
    action:   () => {
      if (isVisible(c)) _visibleColumns.delete(c.name);
      else              _visibleColumns.add(c.name);
      applyColumnVisibility();
      gantt.render();
      persistState();
    },
  }));
  showContextMenu(x, y, items);
}

// Rebuild Gantt data from caches with current filters and reparse.
function rerenderGantt() {
  // Multi-select → fever zone → status (AND of all three)
  let visibleProjects = _allProjects
    .filter(p => _selectedIds.has(p.id))
    // queued/backlog ещё не имеют baseline → fever_zone = null. Такие проекты
    // fever-фильтр пропускает всегда (фильтр применим только к проектам с зоной).
    .filter(p => !p.fever_zone || _filters.fever.has(p.fever_zone))
    .filter(p => {
      if (_filters.statusActive && ['active', 'queued', 'paused'].includes(p.status)) return true;
      if (_filters.statusDone   && ['done', 'cancelled'].includes(p.status))           return true;
      return false;
    });

  // Task-level filtering. Done-чекбокс прячет done/cancelled задачи даже
  // внутри active-проектов (project-уровень фильтра — на projects выше).
  // CC-only: keep only tasks on critical chain (project summary + buffer remain via buildGanttData).
  let tasks = _allTasks;
  if (!_filters.statusDone) {
    tasks = tasks.filter(t => !['done', 'cancelled'].includes(t.status));
  }
  if (_filters.ccOnly) {
    tasks = tasks.filter(t => t.is_on_critical_chain);
  }

  const tasksWithDates  = computeSchedule(visibleProjects, tasks, _allDeps);
  // unfilteredTasksWithDates — все задачи проекта со schedule (без Done/CC-фильтра).
  // Используется в buildGanttData для агрегации MIN/MAX project-row, чтобы бар
  // проекта не сжимался при снятии Done-фильтра. План B (2026-05-09).
  const unfilteredTasksWithDates = computeSchedule(visibleProjects, _allTasks, _allDeps);
  const { data, links } = buildGanttData(visibleProjects, tasksWithDates, _allDeps, _resourcesCache, unfilteredTasksWithDates);

  // Расширяем диапазон chart'а чтобы включал baseline-полоску. По умолчанию
  // DHTMLX auto-fit'ит timeline по start_date/end_date из data (planned
  // даты), а baseline рендерится через отдельный layer и в auto-fit не
  // попадает → если baseline уехал за planned (slip / правка Баз.Начала на
  // дату вне видимого окна), полоска обрезается, а posFromDate бьёт assert
  // «Invalid day index». Вручную считаем MIN/MAX по всем источникам — planned,
  // current baseline (baselineStart/End) и first baseline-полоска — и ставим
  // в gantt.config.{start_date,end_date} перед parse.
  const _DAY = 86400000;
  const _bounds = [];
  const _pushBound = (v) => {
    if (!v) return;
    const t = new Date(v).getTime();
    if (!Number.isNaN(t)) _bounds.push(t);
  };
  for (const d of data) {
    _pushBound(d.start_date);
    _pushBound(d.end_date);
    _pushBound(d.baselineStart);
    _pushBound(d.baselineEnd);
    _pushBound(d.first_baseline_start_date);
    _pushBound(d.first_baseline_end_date);
  }
  if (_bounds.length) {
    const min = Math.min(..._bounds);
    const max = Math.max(..._bounds);
    gantt.config.start_date = new Date(min - 7 * _DAY);
    gantt.config.end_date   = new Date(max + 7 * _DAY);
  } else {
    gantt.config.start_date = null;
    gantt.config.end_date   = null;
  }

  // Закрываем inline editor ДО clearAll: иначе DHTMLX держит ссылку на
  // удалённую row и бьёт assert «Task not found id=...» при следующем
  // mouse-event над освободившейся cell. Симптом — toast спам в проде.
  if (gantt.ext?.inlineEditors?.hide) gantt.ext.inlineEditors.hide();
  gantt.clearAll();
  gantt.parse({ data, links });
  // M4: восстановить свёрнутое состояние сводных задач после parse (DHTMLX
  // сбрасывает $open при clearAll). gantt.close() безопасен на task без
  // children — DHTMLX тихо игнорирует.
  const closed = getClosedTaskIds();
  for (const id of closed) {
    const candidates = [id, Number(id)].filter(v => v !== null && !Number.isNaN(v));
    for (const c of candidates) {
      if (gantt.isTaskExists?.(c)) { gantt.close(c); break; }
    }
  }
  // После любого rerender освежаем подсветку baseline-кнопок (🔓/🔒/🔄):
  // состояние замка зависит от текущих _allTasks, и lock/unlock/rebaseline
  // меняют его «втихую», не вызывая selectedIds change → persistState.
  _refreshBaselineButtons();

  // Ветка 3 плана 2026-05-13-wbs-3level-and-pipeline-ux.md: empty-state
  // overlay для одиночного queued-проекта без задач. Без overlay канвас
  // пустой и выглядит как баг — пользователь не понимает с чего начать.
  installEmptyStateOverlay(visibleProjects);
}

// Empty-state overlay для одиночного queued-проекта без задач.
// Рисуется поверх правой timeline-части DHTMLX (.gantt_task) — оставляя
// заголовок проекта в гриде слева видимым.
function installEmptyStateOverlay(visibleProjects) {
  const ganttHost = document.getElementById('ccpm-gantt-host');
  if (!ganttHost) return;

  // Уберём старый overlay в любом случае — будет пересоздан если нужен.
  const existing = ganttHost.querySelector('.ccpm-empty-state-overlay');
  if (existing) existing.remove();

  // Условия: ровно один проект в visibleProjects, status='queued',
  // нет задач этого проекта (учитываем cancelled — даже если все задачи
  // отменены, проект «пустой» для пользователя).
  if (visibleProjects.length !== 1) return;
  const proj = visibleProjects[0];
  if (proj.status !== 'queued') return;
  const projectTasks = _allTasks.filter(t => t.project_id === proj.id && t.status !== 'cancelled');
  if (projectTasks.length > 0) return;

  const timeline = ganttHost.querySelector('.gantt_task');
  if (!timeline) return;

  const overlay = document.createElement('div');
  overlay.className = 'ccpm-empty-state-overlay';
  overlay.innerHTML = `
    <div class="ccpm-empty-state-card">
      <div class="ccpm-empty-state-title">Проект пуст</div>
      <div class="ccpm-empty-state-text">
        Чтобы начать — добавьте задачи через <b>+ Новая задача</b> в шапке
        или импортируйте структуру из docx через <b>+ Новый проект</b>.
      </div>
      <div class="ccpm-empty-state-hint">
        После создания задач сможете прорабатывать связи, назначать исполнителей
        и оценки. Активация (Pipeline) фиксирует baseline — её делайте, когда
        план готов.
      </div>
    </div>
  `;
  timeline.appendChild(overlay);
}

// ── Background auto-refresh ─────────────────────────────────────────────────
// Pulls fresh tasks from the API every 30s so changes from the Mentor bot
// (and from any other web session) appear without a manual reload.
//
// Skip rules:
//  - tab is hidden (visibilitychange resumes it)
//  - user navigated away from /gantt (interval also stopped)
//  - inspector has unsaved diff for the currently-selected task
//  - tasks payload is byte-equal-ish to the cached one (no rerender)
//
// Scroll position is preserved across the rerender so the user does not
// lose their place when a tick fires while they are scanning the chart.

const _POLL_INTERVAL_MS = 30000;

const _POLL_DIFF_FIELDS = [
  'name', 'status', 'duration_days', 'owner_resource_id', 'sort_order',
  'ruslan_minutes', 'is_on_critical_chain', 'context_flag',
  'planned_start_date', 'actual_minutes_spent', 'notes',
  // Milestone fields (migration 015) — must be in sync with backend response,
  // otherwise polling either storms rerenders (undefined ↔ value mismatch) or
  // misses progress_pct updates from DB triggers.
  'is_milestone', 'parent_task_id', 'deadline', 'deadline_type',
  'feeding_buffer_minutes', 'feeding_buffer_auto', 'progress_pct',
  // Migration 016 — work_pct заменяет progress_pct в смысловой колонке «%».
  'work_pct',
  // Migrations 003 + 017 — baseline-даты (4 новые колонки в gantt-таблице).
  'baseline_start_date', 'baseline_end_date', 'baseline_locked_at',
  // Migration 026 — первоначальная baseline для калибрации и для
  // baseline-полоски на Gantt (LEFT JOIN baseline_history в backend).
  'first_baseline_start_date', 'first_baseline_end_date',
  'first_baseline_ruslan_minutes', 'first_baseline_at',
  // Sub-plan A (2026-05-10): cost_rub участвует в cost-weighted % проекта.
  // Без этого изменения cost_rub через инспектор/Mentor-бот не вызовут rerender.
  'cost_rub',
  // Sub-plan B (миграция 036): затраты — для 6 новых колонок и колонки
  // «Затраты оставш» = planned − actual.
  'expense_planned_rub', 'expense_actual_rub',
  // Sub-plan D (миграция 039): тип CCPM-расчёта + объём в единицах.
  'calc_type', 'scope_units', 'scope_unit_label',
];

function tasksDiffer(prev, next) {
  if (prev.length !== next.length) return true;
  const byId = new Map(prev.map(t => [t.id, t]));
  for (const t of next) {
    const old = byId.get(t.id);
    if (!old) return true;
    for (const f of _POLL_DIFF_FIELDS) {
      const a = old[f] ?? null;
      const b = t[f] ?? null;
      if (a !== b) return true;
    }
    // budget_rub may be string/number depending on serialization
    if (Number(old.budget_rub ?? 0) !== Number(t.budget_rub ?? 0)) return true;
  }
  return false;
}

async function refetchAndMerge() {
  if (!location.pathname.startsWith('/gantt')) {
    stopPolling();
    return;
  }
  if (document.hidden) return;

  let fresh;
  try {
    fresh = await api.tasks(_taskFetchParams);
  } catch (e) {
    return; // network blip, retry on next tick
  }
  if (!tasksDiffer(_allTasks, fresh)) return;

  // Don't disturb user mid-edit. Проверяем оба инспектора — если у юзера
  // есть несохранённые правки (task ИЛИ project), пропускаем rerender, чтобы
  // не затереть форму. Refetch попробует снова через _POLL_INTERVAL_MS.
  if (_selectedTaskId !== null) {
    try {
      const diff = getInspectorDiff();
      if (diff && Object.keys(diff).length > 0) return;
    } catch { /* inspector closed concurrently — fall through */ }
  }
  if (_selectedProjectId !== null) {
    try {
      const diff = getProjectInspectorDiff();
      if (diff && Object.keys(diff).length > 0) return;
    } catch { /* inspector closed concurrently — fall through */ }
  }

  const scrollState = gantt.getScrollState ? gantt.getScrollState() : null;
  _allTasks = fresh;
  rerenderGantt();
  if (scrollState && gantt.scrollTo) gantt.scrollTo(scrollState.x, scrollState.y);
}

function startPolling() {
  if (_pollIntervalId) clearInterval(_pollIntervalId);
  _pollIntervalId = setInterval(refetchAndMerge, _POLL_INTERVAL_MS);
}

function stopPolling() {
  if (_pollIntervalId) {
    clearInterval(_pollIntervalId);
    _pollIntervalId = null;
  }
}

function attachVisibilityListenerOnce() {
  if (_visibilityListenerAttached) return;
  _visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPolling();
    } else if (location.pathname.startsWith('/gantt')) {
      refetchAndMerge();
      startPolling();
    }
  });
}

// ── Baseline layer (MS-Project-style) ───────────────────────────────────────
//
// Рисует пунктирную полоску baseline под актуальным баром задачи. Источник
// дат — first_baseline_start_date / first_baseline_end_date (LEFT JOIN
// baseline_history в backend). Layer перерисовывается DHTMLX'ом на каждый
// repaint, поэтому держим callback максимально лёгким — никаких JOIN'ов и
// getBoundingClientRect.
//
// Toggle через checkbox [data-show-baseline] в toolbar; состояние хранится
// в localStorage('gantt:showBaseline') и читается callback'ом по факту
// рендера. Layer регистрируется ОДИН РАЗ за init Gantt'а (не на каждый
// rerender) — иначе слои дублировались бы.

const BASELINE_TOGGLE_KEY = 'gantt:showBaseline';
const CLOSED_TASKS_KEY = 'gantt:closedTasks';
let _baselineLayerInstalled = false;
let _milestoneDateLayerInstalled = false;
let _baselineLinksLayerInstalled = false;
let _summaryLabelsLayerInstalled = false;
let _closedStateAttached = false;

// M4 (Руслан 2026-05-10): persist свёрнутого состояния сводных задач.
// rerenderGantt() делает clearAll()+parse() — DHTMLX забывает open/close.
// Храним id закрытых задач в localStorage; при rerender применяем поверх.
function getClosedTaskIds() {
  try {
    const raw = localStorage.getItem(CLOSED_TASKS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}
function setClosedTaskIds(set) {
  localStorage.setItem(CLOSED_TASKS_KEY, JSON.stringify([...set]));
}

// N3 (Руслан 2026-05-10): формат подписи даты вехи — DD.MM.YY (как в гриде).
function formatRussianDateShort(d) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear() % 100).padStart(2, '0');
  return `${dd}.${mm}.${yy}`;
}

function isBaselineVisible() {
  // По умолчанию выкл, чтобы не пугать пользователя визуальным шумом до того
  // как он осознанно включит. Toggle persists через localStorage.
  return localStorage.getItem(BASELINE_TOGGLE_KEY) === '1';
}

// При открытом замочке проекта baseline должен показываться автоматически —
// это семантический сигнал «правлю эталон», baseline и есть основной план.
// Атрибут ставится на `[data-baseline-group]` в refreshBaselineButtons /
// onBaselineLockToggle, когда proj.baseline_locked === FALSE.
function isBaselineEditMode() {
  const baseGroupEl = document.querySelector('[data-baseline-group]');
  return baseGroupEl?.dataset?.editMode === 'baseline';
}

function setBaselineVisible(on) {
  localStorage.setItem(BASELINE_TOGGLE_KEY, on ? '1' : '0');
  // Repaint без полной reload — layer просто перечитает флаг.
  gantt.render();
}

// Roxber Teal: corner brackets `[ ]` + позиционирование project-bar.
// DHTMLX рисует project-summary в `.gantt_bars_area` (отдельный контейнер
// с собственным offset). bar.style.top относится к этому контейнеру,
// row.style.top — к .gantt_task_data. Поэтому простое присваивание
// bar.style.top = row.style.top даёт неправильную визуальную y.
//
// Используем delta-метод: считаем разницу в screen-координатах через
// getBoundingClientRect и применяем как delta к текущему bar.style.top.
//
// F27 (2026-05-10): обрабатываем ОБА типа bar-контейнеров (project + summary).
// F28 (2026-05-10): ВСЕ бары (включая обычные .gantt_bar_task — cancelled,
//   todo, done, in_progress). После F26 fix bars_area position разные
//   sub-layers DHTMLX'а отдают разный y-offset → бары съезжают на 1-2 строки
//   вниз. Без delta-метода видимо только когда бар покрыт другим визуалом
//   (cancelled-штриховка, summary-полоска), но математика та же.
//   Также запускается на onGanttScroll — иначе при vertical scroll бары
//   возвращаются к DHTMLX-позиции и «разбегаются» от своих строк.
function installProjectBrackets() {
  const taskArea = document.querySelector('.gantt_task');
  if (!taskArea) return;
  taskArea.querySelectorAll('.ccpm-project-bracket').forEach(n => n.remove());

  // Выравниваем все бары; brackets только для .gantt-project.
  const containers = document.querySelectorAll('.gantt_task_line');
  for (const bar of containers) {
    const taskId = bar.getAttribute('task_id') || bar.dataset.taskId;
    if (!taskId) continue;

    const row = document.querySelector(`.gantt_task_row[task_id="${taskId}"]`)
      || document.querySelector(`.gantt_task_row[data-task-id="${taskId}"]`);
    if (!row) continue;

    // Перемещаем bar в строку через delta-метод (общее для project и summary).
    const barRect = bar.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const targetY = rowRect.top + (rowRect.height - bar.offsetHeight) / 2;
    const deltaY = targetY - barRect.top;
    if (Math.abs(deltaY) > 0.5) {
      const currentTop = parseFloat(bar.style.top || 0);
      bar.style.top = `${currentTop + deltaY}px`;
    }

    // Brackets — ТОЛЬКО для настоящих project-row (gantt-project).
    if (!bar.classList.contains('gantt-project')) continue;

    const left = parseFloat(bar.style.left || 0);
    const top  = parseFloat(bar.style.top || 0);
    const w = bar.offsetWidth;
    const h = bar.offsetHeight;
    if (!Number.isFinite(left) || w <= 0) continue;

    const lb = document.createElement('div');
    lb.className = 'ccpm-project-bracket ccpm-project-bracket--left';
    lb.style.left   = `${left}px`;
    lb.style.top    = `${top}px`;
    lb.style.height = `${h}px`;

    const rb = document.createElement('div');
    rb.className = 'ccpm-project-bracket ccpm-project-bracket--right';
    rb.style.left   = `${left + w - 12}px`;
    rb.style.top    = `${top}px`;
    rb.style.height = `${h}px`;

    bar.parentElement?.appendChild(lb);
    bar.parentElement?.appendChild(rb);
  }
}

// Roxber Teal: подписи КН/НН/КК на середине каждой стрелки-связи.
// DHTMLX рисует link как несколько `.gantt_line_wrapper` div'ов в одном
// контейнере (горизонтальные/вертикальные сегменты). Добавляем поверх
// них одну DOM-плашку с `dep_type` лейблом по центру bbox всех сегментов.
function installDepLabels() {
  const taskArea = document.querySelector('.gantt_task');
  if (!taskArea) return;
  taskArea.querySelectorAll('.ccpm-dep-label').forEach(n => n.remove());

  const links = gantt.getLinks?.() ?? [];
  const linksById = new Map(links.map(l => [String(l.id), l]));

  // DHTMLX 8.x GPL: контейнер связи — `.gantt_task_link[link_id]`.
  // Внутри: один или несколько `.gantt_line_wrapper` (горизонталь/вертикаль
  // отрезки) + `.gantt_link_arrow` (стрелка-наконечник). Координаты сегментов
  // могут быть относительно своего родителя (links area), поэтому используем
  // getBoundingClientRect() и переводим в систему координат `.gantt_task`.
  const taskRect = taskArea.getBoundingClientRect();
  const scrollLeft = taskArea.scrollLeft || 0;
  const scrollTop  = taskArea.scrollTop  || 0;

  const containers = taskArea.querySelectorAll('.gantt_task_link[link_id]');
  for (const container of containers) {
    const id = container.getAttribute('link_id');
    const link = linksById.get(String(id));
    if (!link) continue;

    // Метку ставим на наконечник стрелки (вход в succ-задачу) — это самая
    // надёжная точка: она всегда на линии связи и рядом с потомком, что
    // визуально читается «эта задача связана с предком типом X».
    // Bbox по всем сегментам в сложных Z-/S-формах даёт центр в области
    // без линии (внутри прямоугольника пути) — выглядит «улетевшим».
    const arrow = container.querySelector('.gantt_link_arrow');
    if (!arrow) continue;
    const r = arrow.getBoundingClientRect();
    if (!r.width && !r.height) continue;

    // Смещаем метку на 14px влево от стрелки чтобы наконечник остался видимым.
    const cx = (r.left + r.right) / 2 - taskRect.left + scrollLeft - 14;
    const cy = (r.top + r.bottom) / 2 - taskRect.top  + scrollTop;
    const depType = dhtmlxLinkTypeToDepType(link.type);
    const txt = depTypeShortRu(depType);

    const el = document.createElement('div');
    el.className = 'ccpm-dep-label ccpm-dep-label--' + (depType ?? 'unknown');
    el.textContent = txt;
    el.style.left = `${cx}px`;
    el.style.top  = `${cy}px`;
    taskArea.appendChild(el);
  }
}

// Roxber Teal: вертикальная teal-линия + «TODAY» pill в timeline-scale.
// Реализация без marker-плагина DHTMLX (он Pro-only / отсутствует в
// codebase GPL-сборки). Overlay DOM встраивается в `.gantt_task` (сторона
// timeline) и переcoздаётся на каждый render.
function installTodayOverlay() {
  const taskArea = document.querySelector('.gantt_task');
  if (!taskArea) return;
  // Удаляем старый чтобы не плодить дубли при rerender.
  taskArea.querySelectorAll('.ccpm-today-overlay, .ccpm-today-pill').forEach(n => n.remove());

  const today = new Date();
  let x;
  try {
    x = gantt.posFromDate(today);
  } catch {
    return;
  }
  if (!Number.isFinite(x)) return;

  // 1) Вертикальная линия через всю высоту task-области.
  const line = document.createElement('div');
  line.className = 'ccpm-today-overlay';
  line.style.left = `${x}px`;
  taskArea.appendChild(line);

  // 2) «TODAY» pill в верхнюю шкалу (.gantt_task_scale).
  const scale = taskArea.querySelector('.gantt_task_scale');
  if (scale) {
    const pill = document.createElement('div');
    pill.className = 'ccpm-today-pill';
    pill.textContent = 'TODAY';
    pill.style.left = `${x}px`;
    scale.appendChild(pill);
  }
}

// Zoom-overlay в правом нижнем углу диаграммы (план C 2026-05-09).
// Плавный zoom in/out изменяет min_column_width — масштабирует ТОЛЬКО
// тайм-лайн, не трогая ширину таблицы. Discrete-шкалы Час/День/Неделя/
// Месяц/Квартал в шапке меняют уровень scale (level of detail), это —
// дополнение для тонкой настройки внутри уровня.
const _ZOOM_BASE = 70;     // default min_column_width в DHTMLX
const _ZOOM_MIN_FACTOR = 0.5;
const _ZOOM_MAX_FACTOR = 3.0;
let _zoomFactor = 1.0;

function installZoomOverlay() {
  // F2 fix (2026-05-10): overlay должен быть привязан к .gantt_container
  // (не прокручивается), а НЕ к .gantt_task (прокручивается → overlay уезжает).
  // DHTMLX layout-cells пересоздаются при `gantt.render()`/resize, так что
  // overlay в layout-cell тоже исчезает. .gantt_container остаётся стабилен.
  // Position absolute относительно него с bottom/right → всегда виден внизу
  // справа, не закрывает бары (overlay smaller, в углу, не на bars).
  const ganttRoot = document.querySelector('.gantt_container');
  if (!ganttRoot) return;
  let overlay = ganttRoot.querySelector(':scope > .ccpm-zoom-overlay');
  if (overlay) return;  // уже есть (живёт пока существует .gantt_container)
  overlay = document.createElement('div');
  overlay.className = 'ccpm-zoom-overlay';
  overlay.innerHTML = `
    <button type="button" class="ccpm-zoom-btn" data-zoom="out" title="Уменьшить (бары уже)">−</button>
    <span class="ccpm-zoom-pct" data-zoom-pct>${Math.round(_zoomFactor * 100)}%</span>
    <button type="button" class="ccpm-zoom-btn" data-zoom="in" title="Увеличить (бары шире)">+</button>
    <button type="button" class="ccpm-zoom-btn" data-zoom="reset" title="Сброс на 100%">⌂</button>
  `;
  overlay.addEventListener('click', (e) => {
    const action = e.target.dataset?.zoom;
    if (!action) return;
    if (action === 'in')   _zoomFactor = Math.min(_ZOOM_MAX_FACTOR, _zoomFactor * 1.25);
    if (action === 'out')  _zoomFactor = Math.max(_ZOOM_MIN_FACTOR, _zoomFactor / 1.25);
    if (action === 'reset') _zoomFactor = 1.0;
    gantt.config.min_column_width = Math.round(_ZOOM_BASE * _zoomFactor);
    // Обновим pct сразу (gantt.render синхронный, но безопасно явно)
    const pct = overlay.querySelector('[data-zoom-pct]');
    if (pct) pct.textContent = `${Math.round(_zoomFactor * 100)}%`;
    gantt.render();
  });
  ganttRoot.appendChild(overlay);
}

// gantt.resetLayout() сбрасывает task-layers (DHTMLX dropLayout + rebuild
// пересоздаёт view'ы из gantt.config.layout). Наши overlay-renderer'ы при
// этом теряются, но guard-флаги остаются true → install* функции no-op.
// Этот helper сбрасывает все 4 guard'а и переустанавливает layers.
// Вызывается после каждого gantt.resetLayout().
function reinstallTaskLayers() {
  _baselineLayerInstalled = false;
  _baselineLinksLayerInstalled = false;
  _milestoneDateLayerInstalled = false;
  _summaryLabelsLayerInstalled = false;
  installBaselineLayer();
  installBaselineLinksLayer();
  installMilestoneDateLayer();
  installSummaryLabelsLayer();
}

function installBaselineLayer() {
  if (_baselineLayerInstalled) return;
  // DHTMLX 8.x GPL: gantt.addTaskLayer удалён из public API, доступ только
  // через сервис $services.getService('layers').getDataRender('task').addLayer.
  // Сигнатура — { render: (task) => HTMLElement|null }.
  const layers = gantt.$services?.getService?.('layers');
  const taskRender = layers?.getDataRender?.('task');
  if (!taskRender?.addLayer) {
    console.warn('[calibration] DHTMLX layers API недоступен — baseline-полоска отключена');
    return;
  }
  _baselineLayerInstalled = true;
  taskRender.addLayer({
    renderer: {
      render: (task) => {
        if (!isBaselineVisible() && !isBaselineEditMode()) return null;
        if (task._isBuffer) return null;
        // M3a (Руслан 2026-05-10): для вехи рисуем полупрозрачный baseline-ромб
        // отдельным элементом по `baseline_end_date` (adaptive baseline).
        // Если совпадает с текущей датой ромба — не рисуем (визуально один).
        // N2 (2026-05-10): рядом с ромбом — тусклая подпись даты DD.MM.YY.
        if (task._isMilestone) {
          const blStr = task.baseline_end_date_raw;
          if (!blStr) return null;
          if (blStr === task.start_date) return null;
          const blDate = gantt.date.parseDate(String(blStr), 'xml_date');
          if (!blDate) return null;
          const sizes = gantt.getTaskPosition(task, blDate, blDate);
          const size = 14;
          const wrap = document.createElement('div');
          wrap.style.position = 'absolute';
          wrap.style.left = `${sizes.left - size / 2}px`;
          wrap.style.top  = `${sizes.top + (sizes.rowHeight - size) / 2}px`;
          wrap.style.height = `${size}px`;
          wrap.style.pointerEvents = 'none';
          const diamond = document.createElement('div');
          diamond.className = 'gantt-baseline-milestone';
          diamond.style.width = `${size}px`;
          diamond.style.height = `${size}px`;
          wrap.appendChild(diamond);
          const label = document.createElement('div');
          label.className = 'gantt-baseline-milestone-date';
          label.textContent = formatRussianDateShort(blDate);
          wrap.appendChild(label);
          return wrap;
        }
        // F31 (2026-05-10): overlay = колонка БН/БО. Приоритет baselineStart/End
        // (актуальный adaptive baseline из Phase E), fallback на first_baseline_*.
        // Так overlay на канвасе и колонки в гриде показывают одни даты.
        const start = task.baselineStart || task.first_baseline_start_date;
        const end   = task.baselineEnd   || task.first_baseline_end_date;
        if (!start || !end) return null;
        const startDate = gantt.date.parseDate(String(start), 'xml_date');
        const endDate   = gantt.date.parseDate(String(end),   'xml_date');
        if (!startDate || !endDate) return null;
        const sizes = gantt.getTaskPosition(task, startDate, endDate);
        const el = document.createElement('div');
        // Roxber Teal pattern (MS Project): baseline = серый flat-бокс
        // ШИРЕ актуального бара (26px > 22px), actual sits inside с
        // visual offset. Окраска: ahead = done раньше plan, late = позже.
        let mod = '';
        if (task.status === 'done' && task.completed_at) {
          const completedDate = new Date(task.completed_at);
          mod = completedDate <= endDate ? 'ahead' : 'late';
        }
        el.className = 'gantt-baseline-bar' + (mod ? ` gantt-baseline-bar--${mod}` : '');
        // Locked-режим (замочек закрыт): MS Project pattern — baseline = серая
        // рамка-увеличитель ШИРЕ актуального бара (30px > 22px), actual sits
        // inside с visual offset. Видим только при включённой галочке Baseline.
        //
        // Unlock-режим («правка эталона»): swap — baseline становится размером
        // с обычный task-bar (22px без padX), z-index выше current. CSS делает
        // .gantt_task_line призрачным через [data-edit-mode="baseline"].
        // Заливка baseline остаётся обычной по легенде (--baseline-fill),
        // не яркой — Руслан явно отказался от teal-bright перекраски.
        const swap = isBaselineEditMode();
        const barH = 22;
        const baseH = swap ? 22 : 30;
        const padX = swap ? 0  : 4;
        el.style.left  = `${sizes.left - padX}px`;
        const barTop = (sizes.rowHeight - barH) / 2;
        el.style.top   = `${sizes.top + barTop - (baseH - barH) / 2}px`;
        el.style.width = `${Math.max(sizes.width + padX * 2, 12)}px`;
        el.style.height = `${baseH}px`;
        return el;
      },
    },
  });
}

// Baseline-зависимости: тонкие серые SVG-линии между baseline-bar'ами
// предшественника и наследника. DHTMLX по умолчанию рисует links только
// между основными task-bar'ами — для baseline нужен отдельный overlay.
// Подпись типа (КН/НН/КК) НЕ добавляем — в baseline-режиме тип уже понятен
// из основного слоя. Виден тот же toggle isBaselineVisible.
function installBaselineLinksLayer() {
  if (_baselineLinksLayerInstalled) return;
  const layers = gantt.$services?.getService?.('layers');
  const taskRender = layers?.getDataRender?.('task');
  if (!taskRender?.addLayer) return;
  _baselineLinksLayerInstalled = true;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ARROW_SIZE = 5;

  function baselineEndpoints(task) {
    const start = task.baselineStart || task.first_baseline_start_date;
    const end   = task.baselineEnd   || task.first_baseline_end_date;
    if (!start || !end) return null;
    const sd = gantt.date.parseDate(String(start), 'xml_date');
    const ed = gantt.date.parseDate(String(end),   'xml_date');
    if (!sd || !ed) return null;
    return gantt.getTaskPosition(task, sd, ed);
  }

  taskRender.addLayer({
    renderer: {
      render: (succ) => {
        if (!isBaselineVisible() && !isBaselineEditMode()) return null;
        if (succ._isBuffer) return null;

        const incoming = _allDeps.filter(d => d.successor_id === succ.id);
        if (incoming.length === 0) return null;

        const succPos = baselineEndpoints(succ);
        if (!succPos) return null;
        const targetX = succPos.left;
        const targetY = succPos.top + succPos.rowHeight / 2;

        const svg = document.createElementNS(SVG_NS, 'svg');
        svg.setAttribute('class', 'gantt-baseline-links-svg');
        svg.style.position = 'absolute';
        svg.style.left = '0';
        svg.style.top = '0';
        svg.style.overflow = 'visible';
        svg.style.pointerEvents = 'none';

        let drew = 0;
        for (const dep of incoming) {
          if (!gantt.isTaskExists(dep.predecessor_id)) continue;
          const pred = gantt.getTask(dep.predecessor_id);
          const predPos = baselineEndpoints(pred);
          if (!predPos) continue;
          const sourceX = predPos.left + predPos.width;
          const sourceY = predPos.top + predPos.rowHeight / 2;
          const line = document.createElementNS(SVG_NS, 'path');
          line.setAttribute('d', `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`);
          line.setAttribute('class', 'gantt-baseline-link');
          svg.appendChild(line);
          const tri = document.createElementNS(SVG_NS, 'polygon');
          tri.setAttribute(
            'points',
            `${targetX},${targetY} ` +
            `${targetX - ARROW_SIZE},${targetY - ARROW_SIZE / 2} ` +
            `${targetX - ARROW_SIZE},${targetY + ARROW_SIZE / 2}`,
          );
          tri.setAttribute('class', 'gantt-baseline-link-arrow');
          svg.appendChild(tri);
          drew++;
        }
        return drew > 0 ? svg : null;
      },
    },
  });
}

// M3b (Руслан 2026-05-10): подпись даты справа от ромба вехи (как мокап Figma
// «◆ Запуск · 22 май»). Отдельный layer, не зависит от галочки Baseline —
// показывается всегда, потому что у вехи это её главный показатель. Overdue —
// красный (var(--fever-red)).
function installMilestoneDateLayer() {
  if (_milestoneDateLayerInstalled) return;
  const layers = gantt.$services?.getService?.('layers');
  const taskRender = layers?.getDataRender?.('task');
  if (!taskRender?.addLayer) return;
  _milestoneDateLayerInstalled = true;
  taskRender.addLayer({
    renderer: {
      render: (task) => {
        if (!task._isMilestone) return null;
        if (!task.start_date) return null;
        const d = task.start_date instanceof Date
          ? task.start_date
          : gantt.date.parseDate(String(task.start_date), 'xml_date');
        if (!d) return null;
        const sizes = gantt.getTaskPosition(task, d, d);
        const el = document.createElement('div');
        el.className = 'gantt-milestone-date' + (task._isOverdue ? ' overdue' : '');
        el.textContent = formatRussianDateShort(d);
        // 14px справа от центра ромба (ромб DHTMLX ~12px wide).
        el.style.left = `${sizes.left + 14}px`;
        el.style.top  = `${sizes.top + (sizes.rowHeight - 16) / 2}px`;
        return el;
      },
    },
  });
}

// Лейблы на сводных (corner-brackets `[ ]` bar):
// - % выполнения (work_pct) — на линии перед финишем, как у обычной задачи.
// - planned_end_date — после bar справа, как у вех.
// Через overlay вместо gantt.templates.task_text/rightside_text, т.к. для
// type='project' DHTMLX иногда игнорирует templates, а позиция «перед концом
// bar» требует точного расчёта по sizes.width.
function installSummaryLabelsLayer() {
  if (_summaryLabelsLayerInstalled) return;
  const layers = gantt.$services?.getService?.('layers');
  const taskRender = layers?.getDataRender?.('task');
  if (!taskRender?.addLayer) return;
  _summaryLabelsLayerInstalled = true;
  taskRender.addLayer({
    renderer: {
      render: (task) => {
        if (!task._isSummary) return null;
        if (!task.start_date || !task.end_date) return null;
        const s = task.start_date instanceof Date
          ? task.start_date
          : gantt.date.parseDate(String(task.start_date), 'xml_date');
        const e = task.end_date instanceof Date
          ? task.end_date
          : gantt.date.parseDate(String(task.end_date), 'xml_date');
        if (!s || !e) return null;
        const sizes = gantt.getTaskPosition(task, s, e);
        const wrap = document.createElement('div');
        wrap.style.position = 'absolute';
        wrap.style.left = `${sizes.left}px`;
        wrap.style.top  = `${sizes.top}px`;
        wrap.style.height = `${sizes.rowHeight}px`;
        wrap.style.pointerEvents = 'none';
        // z-index 5 — поверх .gantt_task_line (DHTMLX по умолчанию ~2),
        // иначе % уходит под полупрозрачный navy bar сводной и виден неполностью.
        wrap.style.zIndex = '5';
        // % выполнения — правый край совпадает с правым краем bar минус 8px
        // (внутри правого corner-bracket). Translate -100% по X, так что
        // координата left = правый край bar = sizes.width - 8.
        if (task.work_pct != null) {
          const pctEl = document.createElement('span');
          pctEl.className = 'ccpm-pctlabel ccpm-summary-pct';
          pctEl.style.position = 'absolute';
          pctEl.style.left = `${Math.max(sizes.width - 8, 0)}px`;
          pctEl.style.top  = '50%';
          pctEl.style.transform = 'translate(-100%, -50%)';
          pctEl.textContent = `${Math.round(Number(task.work_pct))}%`;
          wrap.appendChild(pctEl);
        }
        // Дата окончания — за правым краем bar с gap 6px.
        const dateEl = document.createElement('span');
        dateEl.className = 'gantt-milestone-date ccpm-summary-date';
        dateEl.style.position = 'absolute';
        dateEl.style.left = `${sizes.width + 6}px`;
        dateEl.style.top  = '50%';
        dateEl.style.transform = 'translateY(-50%)';
        dateEl.textContent = formatRussianDateShort(e);
        wrap.appendChild(dateEl);
        return wrap;
      },
    },
  });
}

// ── Gantt configuration ──────────────────────────────────────────────────────

function configureGantt() {
  gantt.config.date_format  = '%Y-%m-%d';
  gantt.config.scale_unit   = 'week';
  gantt.config.step         = 1;
  gantt.config.date_scale   = 'Нед %W';
  gantt.config.row_height   = 36;
  gantt.config.bar_height   = 22;
  gantt.config.show_grid    = true;
  gantt.config.grid_width   = _gridWidth || 420;
  // 2026-05-11: разрешить splitter'у сжимать grid меньше суммы колонок.
  // По дефолту DHTMLX подгоняет grid_width под cols и блокирует drag влево
  // за «последний столбец». С false grid живёт своей жизнью, лишние
  // колонки уезжают за свой собственный gridScroll (см. layout ниже).
  gantt.config.keep_grid_width = false;
  gantt.config.min_grid_width  = 1;
  gantt.config.readonly     = false;
  gantt.config.drag_move    = true;
  gantt.config.drag_resize  = true;
  gantt.config.drag_links     = true;
  gantt.config.drag_progress  = false;
  gantt.config.details_on_dblclick = false;
  // Защита от случайного создания task через DHTMLX-lightbox при drag-link к
  // пустому месту: lightbox не вызывается на create. Свой инспектор открывается
  // только через явную кнопку «+ Новая задача» / клавишу Insert / inline-edit.
  gantt.config.details_on_create = false;
  gantt.config.inline_editing = true;
  gantt.config.open_tree_initially = true;
  // Регистрируем кастомный money editor (определён выше — moneyEditor object).
  // Используется в COLUMN_DEFS для cost_rub и expense_*_rub.
  if (gantt.config.editor_types) {
    gantt.config.editor_types.money = moneyEditor;
  }
  // Phase 5 — manual vertical sort within a project. `order_branch_free=false`
  // keeps reparenting to a different project off (cross-project moves require
  // schedule recompute and aren't in scope here).
  gantt.config.order_branch       = true;
  gantt.config.order_branch_free  = false;
  // dhtmlx-gantt 8.x GPL: column reorder есть (через ColumnDnd), column
  // resize — нет (grid_resize.gpl.js — заглушка). Resize реализован
  // вручную в attachCustomColumnResize() через mousedown на правом крае
  // .gantt_grid_head_cell с e.stopPropagation() — не конфликтует с reorder.
  gantt.config.reorder_grid_columns = true;

  // План C (2026-05-09): splitter между таблицей и календарём + отдельный
  // horizontal scroll в таблице. По умолчанию DHTMLX обрезает grid жёсткой
  // grid_width — горизонтальный скролл недоступен, и колонки `Дни/Ресурс/CC/%`
  // невидимы. С этим layout: drag-resizer перетаскивает границу, gridScroll
  // даёт собственный horizontal scrollbar в таблице.
  // 2026-05-12: каждый view + его horizontal scrollbar обёрнуты в свой
  // `rows`-блок, чтобы scrollbar шёл ровно под своей панелью (раньше оба
  // scrollbar'а в плоском row растягивались на всю ширину gantt'а и
  // сливались в один). `{resizer: true}` в GPL-сборке не рендерится
  // (resizer_cell.gpl.js → null) — drag границы grid|timeline делается
  // своим div'ом в attachCustomGridSplitter().
  gantt.config.layout = {
    css: 'gantt_container',
    cols: [
      {
        // 2026-05-12: id + width на rows-обёртке. После обёртывания в rows
        // ширина grid'а управляется НЕ через gantt.config.grid_width
        // (DHTMLX сбрасывает его на render), а через $config.width этой
        // самой layout-cell. Splitter меняет именно её.
        id: 'gridArea',
        width: _gridWidth || 420,
        rows: [
          { view: 'grid', scrollX: 'gridScroll', scrollY: 'scrollVer', scrollable: true },
          { view: 'scrollbar', id: 'gridScroll' },
        ],
      },
      {
        rows: [
          { view: 'timeline', scrollX: 'scrollHor', scrollY: 'scrollVer' },
          { view: 'scrollbar', id: 'scrollHor' },
        ],
      },
      { view: 'scrollbar', id: 'scrollVer' },
    ],
  };

  applyColumnVisibility();

  // Task bar color class
  gantt.templates.task_class = (_s, _e, task) => getTaskClass(task);
  // V1 fix (план B 2026-05-09): link к/от cancelled задач — приглушённый.
  gantt.templates.link_class = (link) => link?._muted ? 'gantt-link-muted' : '';

  // Cell background: weekend / recurring / today (Roxber Teal).
  gantt.templates.timeline_cell_class = (_task, date) => {
    // Today highlight (мокап `.th.today` — teal-tinted background)
    const today = new Date();
    if (date.getFullYear() === today.getFullYear()
        && date.getMonth() === today.getMonth()
        && date.getDate() === today.getDate()) {
      return 'today-cell';
    }
    const dow = jsToIsoDow(date.getDay());
    if (dow === 6 || dow === 7) return 'weekend-cell';
    if (_recurringDows.has(dow)) return 'recurring-cell';
    return '';
  };

  // Selected row highlight (Phase 1 — driven by _selectedTaskId).
  gantt.templates.grid_row_class = (_s, _e, task) => (
    _selectedTaskId === task.id ? 'gantt-row-selected' : ''
  );
  gantt.templates.task_row_class = (_s, _e, task) => (
    _selectedTaskId === task.id ? 'gantt-row-selected' : ''
  );

  // Tooltip
  gantt.templates.tooltip_text = (_s, _e, task) => {
    if (task.type === gantt.config.types.project) return task.text;
    if (task._isMilestone) {
      const dlType = task._deadlineType === 'hard' ? 'жёсткий' : 'мягкий';
      const overdue = task._isOverdue ? '<span style="color:#f85149">⚠ ПРОСРОЧЕНА</span><br>' : '';
      const pct = task.progress != null ? Math.round(Number(task.progress) * 100) : 0;
      return `<b>🔶 Веха: ${task.text}</b><br>
        Дедлайн: ${task.start_date ? formatDateDisplay(task.start_date) : '—'} (${dlType})<br>
        Прогресс: ${pct}%<br>
        ${overdue}${task._cc ? '<span style="color:#f85149">● Критическая цепь</span>' : ''}`;
    }
    if (task._isFeedingBuffer) return `<b>${task.text}</b><br>Защищает дедлайн вехи`;
    return `<b>${task.text}</b><br>
      Ресурс: ${task.resource || '—'}<br>
      Длит.:  ${task.duration} дн.<br>
      ${task._cc ? '<span style="color:#f85149">● Критическая цепь</span><br>' : ''}
      ${task._status ? 'Статус: ' + task._status : ''}`;
  };

  // "N%" label справа от bar'а. Roxber Teal: имя задачи в bar НЕ
  // отображается (только в left tree), внутри bar только проценты.
  // F25 устаревший: для summary % и дата окончания теперь рисуются через
  // installSummaryLabelsLayer (overlay), позиции считаются по sizes.width
  // точнее, чем DHTMLX-template'ы (которые для type='project' могут
  // игнорироваться или давать нежелательный offset).
  gantt.templates.rightside_text = () => '';

  // Roxber Teal task_text:
  // - project — название проекта (внутри translucent navy bar);
  // - summary — пусто (% уже в rightside_text, имя — в grid-колонке);
  // - milestone — пусто (ромб без подписи);
  // - buffer — текст BUFFER (uppercase через CSS);
  // - phase — название фазы;
  // - обычная задача — `<span class="ccpm-pctlabel">N%</span>` справа bar'а.
  //   Pseudo-pseudo `.pctlabel` мокапа: 10.5px white-92 bold с тенью.
  gantt.templates.task_text = (_s, _e, task) => {
    if (task._isSummary)   return '';  // % через rightside_text (bar 4px узкий)
    if (task.type === gantt.config.types.project) return task.text;
    if (task._isMilestone) return '';
    if (task._isBuffer)    return task.text;
    if (task.progress == null) return '';
    const pct = Math.round(Number(task.progress) * 100);
    return `<span class="ccpm-pctlabel">${pct}%</span>`;
  };

}

// ── Data builder ─────────────────────────────────────────────────────────────

function buildGanttData(projects, tasks, deps, resources, allTasks = null) {
  // allTasks — необязательный «несуррогатный» список всех задач проекта (без
  // Done/CC-фильтра). Используется для агрегации MIN/MAX project-row, чтобы
  // бар проекта оставался стабильным при снятии Done-фильтра. План B 2026-05-09.
  // Если не передан — берём tasks (легаси-поведение).
  const aggregateTasks = allTasks ?? tasks;
  const resourceMap = Object.fromEntries(resources.map(r => [r.id, r.name]));
  const ganttTasks  = [];
  const links       = [];
  let   linkId      = 1;

  // Build pred/succ maps once per re-render. Объекты включают dep_type, чтобы
  // колонка Pred/Succ показывала маркер типа `87(НН)` для не-FS связей.
  const predBy = {};
  const succBy = {};
  for (const d of deps) {
    (predBy[d.successor_id]   ??= []).push({ id: d.predecessor_id, dep_type: d.dep_type });
    (succBy[d.predecessor_id] ??= []).push({ id: d.successor_id,   dep_type: d.dep_type });
  }
  // Для FS-связи (default) — голый id; для остальных — `87(НН)` / `87(КК)`.
  // Inline-edit понимает оба формата через RU_TO_DEP_TYPE (utils/dep-types.js).
  const fmtDeps = (items) => (items ?? []).map(i =>
    i.dep_type && i.dep_type !== 'finish_to_start'
      ? `${i.id}(${depTypeShortRu(i.dep_type)})`
      : String(i.id)
  ).join(',');

  for (const proj of projects) {
    const projTasks = tasks.filter(t => t.project_id === proj.id);
    // Несуррогатный набор задач проекта — для агрегации project-row,
    // чтобы при Done-фильтре бар проекта не сжимался. Если allTasks не
    // передан — fallback на projTasks (легаси).
    const projTasksAll = aggregateTasks.filter(t => t.project_id === proj.id);

    // Пустой проект (только что создан, задач ещё нет) — показываем
    // placeholder-bar на 1 день начиная с сегодня, чтобы пользователь видел
    // строку в дереве и мог добавить задачи через «+ Новая задача».
    if (!projTasksAll.length) {
      const today = new Date();
      const tomorrow = new Date(today.getTime() + 86400000);
      ganttTasks.push({
        id:         `p_${proj.id}`,
        text:       `${feverEmoji(proj.fever_zone)} ${proj.name} (пусто — добавь задачи)`,
        start_date: formatDate(today),
        end_date:   formatDate(tomorrow),
        type:       gantt.config.types.project,
        open:       true,
        _status:    proj.status,
        _cc:        false,
        _isBuffer:  false,
      });
      continue;
    }

    // Project summary bar — MIN/MAX по ВСЕМ задачам проекта (projTasksAll),
    // не по фильтрованным. Иначе при снятии Done-фильтра бар сжимается.
    // F9 fix (2026-05-10): guard от NaN/undefined в t.start/t.end.
    // computeSchedule может пропустить задачи без planned_*_date и без
    // predecessors → они попадают с {start: today, end: today+1} (валидно),
    // но cancelled/новые задачи могут иметь Invalid Date → Math.min(NaN)
    // = NaN → new Date(NaN) → DHTMLX сместил project-bar в дефолтный год
    // (~2027). Filter валидные даты ПЕРЕД агрегацией.
    const validStarts = projTasksAll
      .map(t => t.start)
      .filter(d => d instanceof Date && !isNaN(d.getTime()))
      .map(d => d.getTime());
    const validEnds = projTasksAll
      .map(t => t.end)
      .filter(d => d instanceof Date && !isNaN(d.getTime()))
      .map(d => d.getTime());
    if (!validStarts.length || !validEnds.length) {
      // Fallback: если нет валидных дат вообще — placeholder на 1 день от сегодня.
      const today = new Date();
      const tomorrow = new Date(today.getTime() + 86400000);
      ganttTasks.push({
        id:         `p_${proj.id}`,
        text:       `${feverEmoji(proj.fever_zone)} ${proj.name} (нет дат)`,
        start_date: formatDate(today),
        end_date:   formatDate(tomorrow),
        type:       gantt.config.types.project,
        open:       true,
        _status:    proj.status,
        _cc:        false,
        _isBuffer:  false,
      });
      continue;
    }
    const projStart = new Date(Math.min(...validStarts));
    const projEnd   = new Date(Math.max(...validEnds));

    // Summary-baseline: агрегат от MIN(first_baseline_start) до MAX(first_baseline_end)
    // среди задач проекта. Игнорируем задачи без first_baseline. Если ни одна
    // задача не залочена — у проектной строки baseline-полоски не будет.
    const fbStarts = projTasksAll.map(t => t.first_baseline_start_date).filter(Boolean);
    const fbEnds   = projTasksAll.map(t => t.first_baseline_end_date).filter(Boolean);
    const projFbStart = fbStarts.length
      ? fbStarts.reduce((min, d) => d < min ? d : min)
      : null;
    const projFbEnd = fbEnds.length
      ? fbEnds.reduce((max, d) => d > max ? d : max)
      : null;

    // Колонки «Баз. начало», «Баз. окончание», «Дни (баз.)», «Начало»,
    // «Окончание», «%» агрегируем от ВСЕХ задач проекта (стабильно при фильтрах).
    const taskBlStarts = projTasksAll.map(t => t.baseline_start_date).filter(Boolean);
    const taskBlEnds   = projTasksAll.map(t => t.baseline_end_date).filter(Boolean);
    const projBlStart  = taskBlStarts.length ? taskBlStarts.reduce((a,b) => a < b ? a : b) : '';
    const projBlEnd    = taskBlEnds.length   ? taskBlEnds.reduce((a,b)   => a > b ? a : b) : '';

    const taskPlStarts = projTasksAll.map(t => t.planned_start_date).filter(Boolean);
    const taskPlEnds   = projTasksAll.map(t => t.planned_end_date).filter(Boolean);
    const projPlStart  = taskPlStarts.length ? taskPlStarts.reduce((a,b) => a < b ? a : b) : '';
    const projPlEnd    = taskPlEnds.length   ? taskPlEnds.reduce((a,b)   => a > b ? a : b) : '';

    // baselineDuration — сумма по задачам (workload в днях) если хоть у одной
    // задачи поле есть. Не считается «по календарю», т.к. отражает план объёма.
    const taskBlDurs = projTasksAll.map(t => t.baseline_duration_days).filter(v => v != null);
    const projBlDur  = taskBlDurs.length
      ? Math.round(taskBlDurs.reduce((s,v) => s + Number(v), 0))
      : '';

    // Sub-plan A (Руслан 2026-05-10): прогресс — cost-weighted в деньгах.
    // % = SUM(cost_rub × work_pct/100) / SUM(cost_rub).
    // Неоценённые задачи (work_pct=NULL) считаются как 0% (раньше игнорировались
    // → selection bias → у p_10 показывало завышенные 84%). Если у проекта НИ
    // одна задача не имеет cost_rub → progress=null → колонка пустая (проект
    // не оценён по деньгам, нечего показывать). Cancelled и milestone не
    // включаются в расчёт.
    const tasksForProgress = projTasksAll.filter(
      t => !t.is_milestone && t.status !== 'cancelled'
    );
    let projProgress = null;
    const projDenom = tasksForProgress.reduce((s, t) => s + Number(t.cost_rub ?? 0), 0);
    if (projDenom > 0) {
      const projNum = tasksForProgress.reduce(
        (s, t) => s + Number(t.cost_rub ?? 0) * (Number(t.work_pct ?? 0) / 100),
        0
      );
      projProgress = projNum / projDenom;
    }

    // F13+F14 fix (2026-05-10): унифицируем источник дат для project-row.
    // Bar на канвасе и таблица должны показывать одни и те же даты. Раньше:
    // - bar = `formatDate(projStart/End)` — computed (forward-pass).
    // - table «Начало/Окончание» = `projPlStart/End` — БД-поля planned_*_date.
    // - bar baseline = `projFbStart/End` — first_baseline (snapshot).
    // - table «Баз. начало/окончание» = `projBlStart/End` — current adaptive
    //   baseline (после Phase E).
    // Расходились из-за разных источников. Приоритет: БД-поля (актуальное
    // состояние после _recalculate), computed как safety-net.
    const startDateStr = projPlStart || formatDate(projStart);
    const endDateStr   = projPlEnd   || formatDate(projEnd);
    const blStartStr   = projBlStart || projFbStart || '';
    const blEndStr     = projBlEnd   || projFbEnd   || '';

    ganttTasks.push({
      id:         `p_${proj.id}`,
      // Sub-plan C: ручной номер проекта (миграция 038). NULL → шаблон
      // колонки ID показывает системный `p_NN`.
      project_code: proj.project_code ?? null,
      text:       `${feverEmoji(proj.fever_zone)} ${proj.name}`,
      start_date: startDateStr,
      end_date:   endDateStr,
      type:       gantt.config.types.project,
      open:       true,
      _status:    proj.status,
      _cc:        false,
      _isBuffer:  false,
      status:     proj.status,
      progress:   projProgress,
      baselineStart:    blStartStr,
      baselineEnd:      blEndStr,
      baselineDuration: projBlDur,
      plannedStart:     startDateStr,
      plannedEnd:       endDateStr,
      first_baseline_start_date: blStartStr,  // baseline-полоска на канвасе = таблица
      first_baseline_end_date:   blEndStr,
      // Sub-plan B (2026-05-10): агрегаты Объёма и Затрат на project-row.
      // SUM по non-milestone non-cancelled задачам. work_pct учитывается
      // через template (cost_rub × work_pct/100) — здесь передаём «сырые»
      // суммы, шаблон колонок сам посчитает «факт» и «в работе».
      cost_rub: tasksForProgress.reduce((s, t) => s + Number(t.cost_rub ?? 0), 0) || null,
      // Для project-row work_pct = projProgress × 100 (cost-weighted), чтобы
      // template volume_done/remaining дал согласованную сумму.
      work_pct: projProgress != null ? projProgress * 100 : null,
      expense_planned_rub: projTasksAll.reduce((s, t) => s + Number(t.expense_planned_rub ?? 0), 0) || null,
      expense_actual_rub:  projTasksAll.reduce((s, t) => s + Number(t.expense_actual_rub  ?? 0), 0) || null,
    });

    // Orphan-recovery: tasks whose parent_task_id points at a cancelled milestone
    // are reparented to the project. This avoids ghost rows in the tree when a
    // milestone is soft-cancelled but its children remain.
    const cancelledMs = new Set(
      projTasks.filter(t => t.is_milestone && t.status === 'cancelled').map(t => t.id)
    );
    const effectiveParentId = (t) =>
      (t.parent_task_id != null && !cancelledMs.has(t.parent_task_id))
        ? t.parent_task_id
        : null;

    // Group children by effective parent (null = under project bar).
    // Sort by sort_order (manual reorder) then id. Веха не выпрыгивает выше
    // задач — она остаётся на той позиции, на которой её создали или сделали
    // вехой через /to-milestone (Руслан 2026-05-10).
    const byParent = new Map();
    for (const t of projTasks) {
      if (t.is_milestone && t.status === 'cancelled') continue;
      const key = effectiveParentId(t);
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key).push(t);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id - b.id));
    }

    // Helper to push a single task row (milestone or regular). Self-recursive
    // for sub-milestones (DFS preserves DHTMLX ordering within siblings).
    const pushTask = (t) => {
      const ganttParent = effectiveParentId(t) ?? `p_${proj.id}`;

      // План D (2026-05-09): виртуальная сводная задача — ЛЮБАЯ задача
      // с children рендерится как DHTMLX project-row (контейнер с
      // collapsible phase-bar). DHTMLX сам считает охват по children.
      // Без children — обычный bar или milestone-ромб.
      // Прежнее поведение требовало `is_milestone && hasChildren` — только
      // вехи-фазы становились контейнерами, обычные задачи с children — нет.
      const hasChildren = projTasks.some(
        c => c.parent_task_id === t.id && c.status !== 'cancelled'
      );

      if (hasChildren) {
        const workPct = t.work_pct != null ? Number(t.work_pct) : null;
        const isMilestoneSummary = !!t.is_milestone;
        // Sub-plan A2 (2026-05-10): summary progress = cost-weighted формула по
        // direct children (как у project, но только в пределах своего summary).
        // Не зависит от собственного work_pct task'а — агрегируется от детей.
        const summaryNonMs = projTasks.filter(
          c => c.parent_task_id === t.id && !c.is_milestone && c.status !== 'cancelled'
        );
        const summaryDenom = summaryNonMs.reduce((s, c) => s + Number(c.cost_rub ?? 0), 0);
        let summaryProgress = null;
        if (summaryDenom > 0) {
          const summaryNum = summaryNonMs.reduce(
            (s, c) => s + Number(c.cost_rub ?? 0) * (Number(c.work_pct ?? 0) / 100),
            0
          );
          summaryProgress = summaryNum / summaryDenom;
        }
        // F31 (2026-05-10): унификация бар↔колонка для сводной задачи.
        // Раньше (F29): planned = formatDate(t.start) — computeSchedule.
        // Это давало бар по СОБСТВЕННЫМ датам сводной (которая в БД часто
        // имеет короткий duration), не по охвату детей.
        // Теперь: агрегируем MIN/MAX от non-cancelled direct children.
        // Phase D ограничивает иерархию 2 уровнями, рекурсия не нужна.
        const directChildren = projTasks.filter(
          c => c.parent_task_id === t.id && c.status !== 'cancelled'
        );
        const childStarts = directChildren
          .map(c => c.start)
          .filter(d => d instanceof Date && !isNaN(d.getTime()))
          .map(d => d.getTime());
        const childEnds = directChildren
          .map(c => c.end)
          .filter(d => d instanceof Date && !isNaN(d.getTime()))
          .map(d => d.getTime());
        const aggStart = childStarts.length ? new Date(Math.min(...childStarts)) : t.start;
        const aggEnd   = childEnds.length   ? new Date(Math.max(...childEnds))   : t.end;
        const plannedStartStr  = formatDate(aggStart);
        const plannedEndStr    = formatDate(aggEnd);
        // Baseline-агрегат от children's baseline_*_date (адаптивный baseline
        // из Phase E). Fallback на собственный first_baseline_* (snapshot).
        const childBlStarts = directChildren.map(c => c.baseline_start_date).filter(Boolean);
        const childBlEnds   = directChildren.map(c => c.baseline_end_date).filter(Boolean);
        const baselineStartStr =
          (childBlStarts.length ? childBlStarts.reduce((a,b) => a < b ? a : b) : null)
          ?? t.first_baseline_start_date ?? '';
        const baselineEndStr   =
          (childBlEnds.length ? childBlEnds.reduce((a,b) => a > b ? a : b) : null)
          ?? t.first_baseline_end_date ?? '';
        ganttTasks.push({
          id:           t.id,
          parent:       ganttParent,
          // ◆ префикс только для milestone-сводной (бывшей «фазы»);
          // обычная сводная задача без префикса.
          text:         isMilestoneSummary ? `◆ ${t.name}` : t.name,
          start_date:   plannedStartStr,
          end_date:     plannedEndStr,
          type:         gantt.config.types.project,
          open:         true,
          owner_resource_id: t.owner_resource_id,
          cc:           t.is_on_critical_chain ? '✓' : '',
          // V5 fix (план B): «фаза» в колонке Статус смешивала тип и статус.
          // Теперь: milestone+children → 'веха', обычная+children → 'сводная'.
          status:       isMilestoneSummary ? 'веха' : 'сводная',
          progress:     summaryProgress,
          _workPct:     workPct,
          minutes:      '',
          faktic:       '',
          predecessors: fmtDeps(predBy[t.id]),
          successors:   fmtDeps(succBy[t.id]),
          // F34: parent_task_id для column 'Main' editor — без этого editor
          // читает `undefined` и показывает строку «undefined» в input.
          parent_task_id: t.parent_task_id ?? null,
          // F29: 4 даты для колонок грида (раньше отсутствовали → пустые ячейки).
          plannedStart:  plannedStartStr,
          plannedEnd:    plannedEndStr,
          baselineStart: baselineStartStr,
          baselineEnd:   baselineEndStr,
          // F31: first_baseline_* = baselineStartStr/baselineEndStr → installBaselineLayer
          // показывает overlay в тех же датах что колонка БН/БО.
          first_baseline_start_date: baselineStartStr || null,
          first_baseline_end_date:   baselineEndStr   || null,
          notes:        t.notes ?? '',
          _status:      isMilestoneSummary ? 'phase' : 'summary',
          _cc:          !!t.is_on_critical_chain,
          _isBuffer:    false,
          _isMilestone: isMilestoneSummary,
          _isPhase:     isMilestoneSummary,
          _isSummary:   !isMilestoneSummary,
          _deadlineType: t.deadline_type ?? 'soft',
          // Sub-plan B: агрегаты Объёма и Затрат для summary-row
          // (по тем же direct children что и summaryProgress).
          cost_rub: summaryNonMs.reduce((s, c) => s + Number(c.cost_rub ?? 0), 0) || null,
          // work_pct = summaryProgress × 100 — чтобы template volume_done/remaining
          // считал согласованно с cost-weighted progress.
          work_pct: summaryProgress != null ? summaryProgress * 100 : null,
          expense_planned_rub: directChildren.reduce((s, c) => s + Number(c.expense_planned_rub ?? 0), 0) || null,
          expense_actual_rub:  directChildren.reduce((s, c) => s + Number(c.expense_actual_rub  ?? 0), 0) || null,
        });
        // F15 fix (2026-05-10): рекурсия в children для виртуальной сводной.
        // Без неё DHTMLX не знает что у task есть дети → toggle ▶/▼ не
        // отображается. Раньше рекурсия была только в milestone-ветке ниже —
        // для не-milestone сводных (Phase D) забыли добавить.
        const kids = byParent.get(t.id) ?? [];
        for (const child of kids) pushTask(child);
        return;
      }

      if (t.is_milestone) {
        // Веха: work_pct пересчитывается БД-триггером как AVG(child.work_pct)
        // (миграция 016). Если все дети неоценены — work_pct=0. Если ни одного
        // ребёнка нет — также 0.
        const workPct = t.work_pct != null ? Number(t.work_pct) : null;
        const progressFraction = workPct != null
          ? Math.max(0, Math.min(1, workPct / 100))
          : null;

        // F19 (2026-05-10): fallback цепочка для даты вехи. У вех в БД может
        // не быть deadline (импорт от Mentor-бота создавал вехи без deadline).
        // Без даты ромб на канвасе не рисуется + колонки в таблице пустые.
        const milestoneDateStr = t.deadline
          ?? t.planned_start_date
          ?? formatDate(t.start);  // computed как safety-net
        const dl = milestoneDateStr ? new Date(milestoneDateStr) : t.start;
        ganttTasks.push({
          id:           t.id,
          parent:       ganttParent,
          text:         t.name,
          start_date:   formatDate(dl),
          duration:     0,
          type:         gantt.config.types.milestone,
          open:         true,
          resource:     '',
          owner_resource_id: t.owner_resource_id,
          cc:           t.is_on_critical_chain ? '✓' : '',
          status:       t.deadline_type === 'hard' ? 'веха (hard)' : 'веха (soft)',
          progress:     progressFraction,
          _workPct:     workPct,
          minutes:      '',
          faktic:       '',
          predecessors: fmtDeps(predBy[t.id]),
          successors:   fmtDeps(succBy[t.id]),
          // F34: parent_task_id для column 'Main' editor (см. F34 коммент в summary push).
          parent_task_id: t.parent_task_id ?? null,
          baselineStart: t.baseline_start_date ?? milestoneDateStr ?? '',
          baselineEnd:   t.baseline_end_date ?? milestoneDateStr ?? '',
          baselineDuration: t.baseline_duration_days != null
            ? Math.round(Number(t.baseline_duration_days))
            : 0,  // веха = 0 дней
          // M3a: «чистая» baseline-дата без fallback на deadline. Используется
          // installBaselineLayer чтобы решить, рисовать ли отдельный baseline-
          // ромб (тусклый, при включённой галочке Baseline). Если null — не
          // рисуем; если совпадает со start_date — тоже не рисуем (ромбы слились).
          baseline_end_date_raw: t.baseline_end_date ?? null,
          first_baseline_start_date: t.first_baseline_start_date ?? null,
          first_baseline_end_date:   t.first_baseline_end_date ?? null,
          plannedStart:  t.planned_start_date ?? milestoneDateStr ?? '',
          plannedEnd:    t.planned_end_date ?? milestoneDateStr ?? '',
          // F19: явно передаём deadline в task-объект, чтобы column template
          // fallback `t._isMilestone ? (t.deadline ?? '')` работал.
          deadline:     milestoneDateStr,
          notes:        t.notes ?? '',
          _status:      'milestone',
          _cc:          !!t.is_on_critical_chain,
          _isBuffer:    false,
          _isMilestone: true,
          _deadlineType: t.deadline_type ?? 'soft',
          _isOverdue:   isMilestoneOverdue(t, projTasks),
        });

        // Feeding buffer bar — sibling of the milestone (shares ganttParent),
        // ends at the deadline, length = feeding_buffer_minutes / 480 (8h workday).
        const fbMin = Number(t.feeding_buffer_minutes ?? 0);
        if (fbMin > 0 && t.deadline) {
          const fbDays  = Math.max(fbMin / 480, 0.5);  // min 0.5d for visibility
          const fbEnd   = new Date(t.deadline);
          const fbStart = new Date(fbEnd.getTime() - fbDays * 86400000);
          ganttTasks.push({
            id:         `fbuf_${t.id}`,
            parent:     ganttParent,
            text:       `Feeding buffer (${fbMin} мин)`,
            start_date: formatDate(fbStart),
            end_date:   formatDate(fbEnd),
            duration:   fbDays,
            resource:   '',
            cc:         '',
            _status:    'buffer',
            _cc:        false,
            _isBuffer:  true,
            _isFeedingBuffer: true,
          });
        }

        // Recurse into children of this milestone.
        const kids = byParent.get(t.id) ?? [];
        for (const child of kids) pushTask(child);
        return;
      }

      // Regular task row. work_pct (миграция 016) — субъективная оценка
      // объёма выполненной работы 0..100, выставляется руками в инспекторе.
      // NULL = «явно не оценивалось» — колонка «%» покажет пусто.
      const totalMin = Number(t.ruslan_minutes ?? 0);
      const workPct = t.work_pct != null ? Number(t.work_pct) : null;
      const progressFraction = workPct != null
        ? Math.max(0, Math.min(1, workPct / 100))
        : null;
      // План/Факт по времени — отдельная метрика (Часть 2 плана).
      // Считается ТОЛЬКО для завершённых задач, где факт зафиксирован.
      const actualMin = Number(t.actual_minutes_spent ?? 0);
      const timeOverrunPct = (t.status === 'done' && totalMin > 0 && actualMin > 0)
        ? (actualMin / totalMin) * 100
        : null;

      ganttTasks.push({
        id:           t.id,
        parent:       ganttParent,
        text:         t.name,
        start_date:   formatDate(t.start),
        end_date:     formatDate(t.end),
        duration:     Math.max(Math.round(parseFloat(t.duration_days) || 1), 1),
        resource:     resourceMap[t.owner_resource_id] ?? '—',
        owner_resource_id: t.owner_resource_id,
        cc:           t.is_on_critical_chain ? '✓' : '',
        status:       STATUS_RU[t.status] ?? t.status,
        progress:     progressFraction,
        _workPct:     workPct,
        _timeOverrunPct: timeOverrunPct,
        _overrunHigh: timeOverrunPct != null && timeOverrunPct > 200,
        minutes:      totalMin || '',
        faktic:       t.actual_minutes_spent ?? '',
        predecessors: fmtDeps(predBy[t.id]),
        successors:   fmtDeps(succBy[t.id]),
        // F34: parent_task_id для column 'Main' editor (без — input показывает «undefined»).
        parent_task_id: t.parent_task_id ?? null,
        baselineStart: t.baseline_start_date ?? '',
        baselineEnd:   t.baseline_end_date ?? '',
        // Migration 026 — для baseline-полоски (MS-Project-style) под баром.
        first_baseline_start_date: t.first_baseline_start_date ?? null,
        first_baseline_end_date:   t.first_baseline_end_date ?? null,
        // Fallback на computeSchedule (t.start/t.end) для legacy-задач, где
        // planned_*_date в БД ещё NULL: бар на канвасе всё равно рисуется по
        // computed (через preds), но колонки оставались пустыми — расхождение
        // между баром и таблицей. Editor по-прежнему сохраняет в planned_*_date.
        plannedStart:  t.planned_start_date ?? formatDate(t.start),
        plannedEnd:    t.planned_end_date   ?? formatDate(t.end),
        notes:        t.notes ?? '',
        _status:      t.status,
        _cc:          !!t.is_on_critical_chain,
        _isBuffer:    false,
        // Sub-plan B (миграция 036): данные для 6 колонок Объём + Затраты.
        // На task level — сырые поля задачи. work_pct уже передан выше через
        // progress (но шаблон volume_done использует t.work_pct напрямую).
        cost_rub:           t.cost_rub ?? null,
        work_pct:           t.work_pct ?? null,
        expense_planned_rub: t.expense_planned_rub ?? null,
        expense_actual_rub:  t.expense_actual_rub ?? null,
        // Sub-plan D (миграция 039): тип CCPM-расчёта + scope для fixed_scope.
        calc_type:        t.calc_type ?? null,
        scope_units:      t.scope_units ?? null,
        scope_unit_label: t.scope_unit_label ?? null,
      });
    };

    // DFS from top-level (under project) — milestones, then regular tasks,
    // then descend into each milestone's children.
    const topLevel = byParent.get(null) ?? [];
    for (const t of topLevel) pushTask(t);

    // Project Buffer bar (after last CC task)
    const bufLen = bufferDays(proj);
    if (bufLen > 0) {
      const lastCC    = projTasks
        .filter(t => t.is_on_critical_chain)
        .sort((a, b) => b.end.getTime() - a.end.getTime())[0];
      const bufStart  = lastCC ? lastCC.end : projEnd;
      const bufEnd    = new Date(bufStart.getTime() + bufLen * 86400000);

      ganttTasks.push({
        id:         `buf_${proj.id}`,
        parent:     `p_${proj.id}`,
        text:       `Buffer`,
        start_date: formatDate(bufStart),
        end_date:   formatDate(bufEnd),
        duration:   bufLen,
        resource:   '',
        cc:         '',
        _status:    'buffer',
        _cc:        false,
        _isBuffer:  true,
      });
    }
  }

  // Dependency links — use DB id directly for DELETE round-trips.
  // type зависит от dep_type из БД, чтобы стрелки в timeline и ПКМ-меню
  // корректно отражали SS/FF (а не всегда FS).
  // V1 fix (план B 2026-05-09): links к/от cancelled-задач приглушены
  // через CSS class. В мокапе связь к отменённой выглядит как «мертвая»
  // (серый + полупрозрачный), а не активная teal-линия.
  const taskStatusById = new Map(aggregateTasks.map(t => [t.id, t.status]));
  for (const d of deps) {
    const srcStatus = taskStatusById.get(d.predecessor_id);
    const tgtStatus = taskStatusById.get(d.successor_id);
    const isMuted = srcStatus === 'cancelled' || tgtStatus === 'cancelled';
    links.push({
      id:     d.id,
      source: d.predecessor_id,
      target: d.successor_id,
      type:   depTypeToDhtmlxLinkType(d.dep_type),
      _muted: isMuted,
    });
  }
  void linkId; // legacy counter kept to avoid touching unrelated code paths

  return { data: ganttTasks, links };
}

// ── Interactivity (Phase B+: drag/drop, resize → backend) ───────────────────

function isEditableTask(task) {
  // Only real task rows are editable. Project summaries (id starts with 'p_')
  // and buffers (id starts with 'buf_') are computed visuals.
  return task && !task._isBuffer && task.type !== gantt.config.types.project;
}

function showSaving() {
  if (_savingEl) _savingEl.hidden = false;
}
function hideSaving() {
  if (_savingEl) _savingEl.hidden = true;
}

/**
 * Apply cascade response from POST /api/tasks/{id}/move to the gantt UI.
 * Skips milestones — their position is anchored to `deadline`, not derived
 * from cascade. Backend may include them in the cascade payload as no-ops;
 * we ignore those entries on the frontend to avoid moving the diamond.
 *
 * @param {Array<{task_id:number,new_start:string,new_end:string}>} cascade
 */
function applyCascade(cascade) {
  if (!Array.isArray(cascade) || cascade.length === 0) return;
  for (const c of cascade) {
    if (!gantt.isTaskExists(c.task_id)) continue;
    const t = gantt.getTask(c.task_id);
    if (t._isMilestone) continue;  // diamond is anchored to deadline; skip cascade move
    t.start_date = gantt.date.parseDate(c.new_start, 'xml_date');
    t.end_date   = gantt.date.parseDate(c.new_end,   'xml_date');
    // F32 (2026-05-10): sync grid-data fields. Без этого после drag bar
    // сдвигается, но колонки Начало/Окончание показывают старые даты до
    // полного refresh. Backend cascade response уже содержит new_start/new_end,
    // переиспользуем — без дополнительных API-вызовов.
    t.plannedStart = c.new_start;
    t.plannedEnd   = c.new_end;
    gantt.updateTask(c.task_id);
  }
}

/**
 * Walk up the parent chain in DHTMLX until we hit a project bar (`p_*` string id).
 * Used by onBeforeLinkAdd to compare two tasks "are they in the same project?"
 * after milestones introduced an intermediate hierarchy level.
 */
function getProjectIdFromTask(task) {
  let cur = task;
  while (cur && typeof cur.parent === 'number' && gantt.isTaskExists(cur.parent)) {
    cur = gantt.getTask(cur.parent);
  }
  return cur?.parent;  // string like 'p_11' once we've reached the project row
}

function wireInteractivity() {
  // Suppress lightbox edit on dblclick; we use context menu + modals instead.
  gantt.attachEvent('onTaskDblClick', () => false);

  // Block drag on project summaries / buffers — they are visuals, not tasks.
  // Also block on milestones: deadline can only be moved through the inspector
  // (per user decision in 2026-05-03 milestones plan, Q2: "Drag ромбика запрещён").
  gantt.attachEvent('onBeforeTaskDrag', (id) => {
    const task = gantt.getTask(id);
    if (!isEditableTask(task)) return false;
    if (task._isMilestone) return false;
    _dragBackup = {
      id,
      start_date: new Date(task.start_date),
      end_date:   new Date(task.end_date),
      duration:   task.duration,
    };
    return true;
  });

  gantt.attachEvent('onAfterTaskDrag', async (id, mode) => {
    const task = gantt.getTask(id);
    if (!isEditableTask(task)) return;

    const isHourScale = gantt.config.scale_unit === 'hour';
    showSaving();
    try {
      if (mode === 'resize') {
        // Backend recalc по dep_type внутри patchTaskAndRefetchIfRecalc:
        // потомки сдвинутся при следующем рендере (плановые даты).
        await patchTaskAndRefetchIfRecalc(id, { duration_days: task.duration });
        showNotification('Длительность сохранена', 'success');
      } else if (mode === 'move') {
        if (isHourScale) {
          await patchTaskAndRefetchIfRecalc(id, {
            planned_start_date: formatDate(task.start_date),
            planned_start_time: formatTime(task.start_date),
          });
          showNotification('Время старта сохранено', 'success');
        } else {
          // Day-scale move: PATCH обоих полей. Раньше шёл через move_task с
          // cascade=true (PL/pgSQL move_task), но тот делал тупой сдвиг
          // потомков на дельту без учёта dep_type/lag_days. PATCH через
          // update_task → recalculate_planned_dates учитывает FS/SS/FF + lag.
          await patchTaskAndRefetchIfRecalc(id, {
            planned_start_date: formatDate(task.start_date),
            planned_end_date:   formatDate(task.end_date),
          });
          showNotification('Сдвиг сохранён', 'success');
        }
      }
    } catch (err) {
      // Rollback the optimistic UI change.
      if (_dragBackup) {
        const t = gantt.getTask(id);
        t.start_date = _dragBackup.start_date;
        t.end_date   = _dragBackup.end_date;
        t.duration   = _dragBackup.duration;
        gantt.updateTask(id);
      }
      const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка сохранения';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка сохранения', 'error');
      console.error('Gantt drag error:', err);
    } finally {
      hideSaving();
      _dragBackup = null;
    }
  });

  // ── Dependency links (Phase C) ─────────────────────────────────────────────

  // Validate before DHTMLX commits the new link to its store.
  gantt.attachEvent('onBeforeLinkAdd', (_id, link) => {
    if (link.source === link.target) {
      showNotification('Нельзя зависеть от самой себя', 'error');
      return false;
    }
    const src = gantt.isTaskExists(link.source) && gantt.getTask(link.source);
    const tgt = gantt.isTaskExists(link.target) && gantt.getTask(link.target);
    if (!src || !tgt || !isEditableTask(src) || !isEditableTask(tgt)) {
      showNotification('Зависимость можно создать только между задачами/вехами', 'error');
      return false;
    }
    // Walk up to the project bar — milestones introduce an intermediate level,
    // so direct `src.parent !== tgt.parent` would block legit dep within a project.
    if (getProjectIdFromTask(src) !== getProjectIdFromTask(tgt)) {
      showNotification('Задачи в разных проектах', 'error');
      return false;
    }
    return true;
  });

  // Persist link on backend; replace temp id with DB id; rollback on error.
  // dep_type определяется DHTMLX-стороной: drag из правого края → '0' (FS),
  // из левого → '1' (SS), правый-в-правый → '2' (FF) — это «MS Project»
  // поведение (вариант B, см. план 2026-05-04). ПКМ-меню на стрелке
  // позволяет сменить тип после создания (вариант A).
  gantt.attachEvent('onAfterLinkAdd', async (tempId, link) => {
    showSaving();
    try {
      const dep = await api.postDependency({
        predecessor_id: link.source,
        successor_id:   link.target,
        dep_type: dhtmlxLinkTypeToDepType(link.type),
      });
      gantt.changeLinkId(tempId, dep.id);
      _allDeps.push(dep);
      // Backend recalc planned_*_date — рефетч задач для отображения новых дат.
      _allTasks = await api.tasks(_taskFetchParams);
      rerenderGantt();
      showNotification('Зависимость добавлена', 'success');
    } catch (err) {
      // Roll back the optimistic link inside silent() so onAfterLinkDelete
      // does not fire and trigger an unwanted API DELETE on the temp id.
      if (gantt.isLinkExists(tempId)) {
        gantt.silent(() => gantt.deleteLink(tempId));
      }
      const isCycle = (err.detail?.message ?? err.detail ?? '').toString().includes('Cycle');
      const msg = isCycle
        ? 'Циклическая зависимость заблокирована'
        : 'Не удалось сохранить зависимость';
      showNotification(msg, 'error');
      if (isCycle) console.warn('Cycle path:', err.detail?.cycle_path);
    } finally {
      hideSaving();
    }
  });

  // Optimistic delete: UI removes the link immediately, we hit DELETE, rollback on error.
  // Deletion API only makes sense for numeric DB ids; temp string ids are skipped
  // (those belong to in-flight create rollbacks already handled above).
  gantt.attachEvent('onAfterLinkDelete', async (id, link) => {
    if (typeof id !== 'number') return;
    showSaving();
    try {
      await api.deleteDependency(id);
      _allDeps = _allDeps.filter(d => d.id !== id);
      // Backend recalc planned_*_date — рефетч задач: после удаления связи
      // потомок может «съехать» к началу проекта или к меньшему ограничению.
      _allTasks = await api.tasks(_taskFetchParams);
      rerenderGantt();
      showNotification('Зависимость удалена', 'success');
    } catch (err) {
      // Restore via silent() so we don't fire onAfterLinkAdd → API POST → loop.
      if (!gantt.isLinkExists(id)) {
        gantt.silent(() => gantt.addLink({
          id, source: link.source, target: link.target, type: link.type,
        }));
      }
      showNotification('Не удалось удалить зависимость', 'error');
      console.error('Dependency delete error:', err);
    } finally {
      hideSaving();
    }
  });

  // ── ПКМ-меню на стрелке зависимости (Часть 5 плана 2026-05-04) ───────────
  // Меню: «Тип: КН/НН/КК» (текущий помечен) + «Удалить связь». Через ПКМ можно
  // изменить тип уже существующей связи, не пересоздавая её. Бэкенд PATCH
  // /api/dependencies/{id} (см. update_dependency) принимает {dep_type}.
  //
  // DHTMLX 8 GPL не имеет события onLinkContextMenu — используем общий
  // onContextMenu(taskId, linkId, event); если передан linkId — это link.
  gantt.attachEvent('onContextMenu', (taskId, linkId, event) => {
    if (linkId == null) return true; // task/blank ПКМ — пропускаем
    if (event) {
      if (event.preventDefault) event.preventDefault();
      if (event.stopPropagation) event.stopPropagation();
    }
    const link = gantt.getLink(linkId);
    if (!link) return false;
    // Memory: linkId из event может быть string при reload-cycle. Coerce.
    const numericId = Number(linkId);
    const currentDepType = dhtmlxLinkTypeToDepType(link.type);
    const setType = async (newDepType) => {
      if (newDepType === currentDepType) return;
      showSaving();
      try {
        await api.patchDependency(numericId, { dep_type: newDepType });
        const idx = _allDeps.findIndex(d => d.id === numericId);
        if (idx >= 0) _allDeps[idx] = { ..._allDeps[idx], dep_type: newDepType };
        // Обновляем DHTMLX link.type силент, потом полная перерисовка чтобы
        // обновились предиктеры в колонках Pred/Succ соседних задач.
        gantt.silent(() => {
          link.type = depTypeToDhtmlxLinkType(newDepType);
          gantt.updateLink(linkId);
        });
        // Бэкенд авто-пересчитал planned_*_date через ccpm.recalculate_planned_dates
        // (миграция 029) — забираем свежие задачи чтобы forward-pass показал
        // обновлённые даты потомков сразу после смены типа связи.
        _allTasks = await api.tasks(_taskFetchParams);
        rerenderGantt();
        showNotification(`Тип связи: ${depTypeShortRu(newDepType)}`, 'success');
      } catch (err) {
        showNotification('Не удалось сменить тип связи', 'error');
        console.error('patchDependency error:', err);
      } finally {
        hideSaving();
      }
    };
    const dot = (t) => t === currentDepType ? ' ●' : '';
    showContextMenu(event.clientX, event.clientY, [
      { label: `Тип: КН — Окончание → Начало${dot('finish_to_start')}`,
        action: () => setType('finish_to_start') },
      { label: `Тип: НН — Начало → Начало${dot('start_to_start')}`,
        action: () => setType('start_to_start') },
      { label: `Тип: КК — Окончание → Окончание${dot('finish_to_finish')}`,
        action: () => setType('finish_to_finish') },
      { label: '🗑 Удалить связь', danger: true,
        action: () => gantt.deleteLink(linkId) },
    ]);
    return false;
  });

  // ── Inline edit (Phase D) ─────────────────────────────────────────────────
  // dhtmlx 8.x exposes inline editor events on gantt.ext.inlineEditors (not the
  // main event bus). 'onSave' fires when the user commits a value (Enter/blur).

  /**
   * Diff-sync dependencies after the user edited Pred/Succ cell. Поддерживает
   * два формата:
   *   "1,3,5"           — все связи finish_to_start (default).
   *   "1:НН, 3:КК, 5"   — с маркерами типа: НН=start_to_start, КК=finish_to_finish, КН=FS.
   *
   * Diff против _allDeps:
   *   - id есть в new и нет в old → CREATE.
   *   - id есть в old и нет в new → DELETE.
   *   - id в обоих, но dep_type разный → PATCH (новый endpoint миграции 016/017,
   *     см. backend/app/api/dependencies.py:update_dependency).
   *
   * Memory: state.id из DHTMLX inline-editor — string. Coerce к Number перед
   * любыми сравнениями с _allTasks[*].id или _allDeps[*].*_id (число).
   * Этот баг уже всплывал три раза за прошлые сессии (см. memory feedback #4).
   */
  const syncDepsFromText = async (state, task) => {
    const isPred = state.columnName === 'predecessors';
    const parse = (s) =>
      String(s ?? '')
        .split(',')
        .map(x => {
          const m = x.trim().match(/^(\d+)(?::([А-ЯA-Z]{2}))?$/i);
          if (!m) return null;
          const id = parseInt(m[1], 10);
          if (!id) return null;
          const marker = (m[2] ?? '').toUpperCase();
          const dep_type = RU_TO_DEP_TYPE[marker] ?? 'finish_to_start';
          return { id, dep_type };
        })
        .filter(Boolean);

    const taskId = Number(state.id);
    const oldList = parse(state.oldValue);
    const newList = parse(state.newValue);
    const oldMap = new Map(oldList.map(o => [o.id, o.dep_type]));
    const newMap = new Map(newList.map(o => [o.id, o.dep_type]));
    const toAdd   = newList.filter(o => !oldMap.has(o.id));
    const toDel   = oldList.filter(o => !newMap.has(o.id));
    const toPatch = newList.filter(o => oldMap.has(o.id) && oldMap.get(o.id) !== o.dep_type);

    const rollback = () => {
      task[isPred ? 'predecessors' : 'successors'] = state.oldValue;
      gantt.silent(() => gantt.updateTask(state.id));
    };
    showSaving();
    try {
      for (const o of toAdd) {
        if (!_allTasks.find(t => t.id === o.id)) {
          throw new Error(`Задача #${o.id} не найдена`);
        }
        const body = isPred
          ? { predecessor_id: o.id, successor_id: taskId, dep_type: o.dep_type }
          : { predecessor_id: taskId, successor_id: o.id, dep_type: o.dep_type };
        const dep = await api.postDependency(body);
        if (dep && dep.id) _allDeps.push(dep);
      }
      for (const o of toDel) {
        const dep = _allDeps.find(d => isPred
          ? d.predecessor_id === o.id && d.successor_id === taskId
          : d.predecessor_id === taskId && d.successor_id === o.id);
        if (!dep) continue;
        await api.deleteDependency(dep.id);
        _allDeps = _allDeps.filter(d => d.id !== dep.id);
      }
      for (const o of toPatch) {
        const dep = _allDeps.find(d => isPred
          ? d.predecessor_id === o.id && d.successor_id === taskId
          : d.predecessor_id === taskId && d.successor_id === o.id);
        if (!dep) continue;
        await api.patchDependency(dep.id, { dep_type: o.dep_type });
        dep.dep_type = o.dep_type;
      }
      // Перерисовать Gantt чтобы стрелки в timeline и колонки Pred/Succ
      // у соседних задач обновились сразу, а не на следующем polling-tick.
      rerenderGantt();
      showNotification('Зависимости обновлены', 'success');
    } catch (err) {
      rollback();
      const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка сохранения зависимостей';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка', 'error');
    } finally {
      hideSaving();
    }
  };

  if (gantt.ext?.inlineEditors) {
    gantt.ext.inlineEditors.attachEvent('onSave', async (state) => {
      if (state.oldValue === state.newValue) return;
      // Race: между открытием editor и save задача могла исчезнуть
      // (rerender, удаление, скрытие проекта в multi-select). DHTMLX
      // assert «Task not found id=...» при getTask() — не пропускаем.
      if (!gantt.isTaskExists(state.id)) return;
      const task = gantt.getTask(state.id);
      if (!isEditableTask(task)) return;

      let body;
      const rollback = (field) => {
        task[field] = state.oldValue;
        gantt.silent(() => gantt.updateTask(state.id));
      };
      if (state.columnName === 'text') {
        body = { name: String(state.newValue).trim() };
        if (!body.name) {
          showNotification('Имя задачи не может быть пустым', 'error');
          rollback('text');
          return;
        }
      } else if (state.columnName === 'duration' || state.columnName === 'baselineDuration') {
        // F33+F36 (2026-05-10): Дни / Дни (Баз.) — inline-edit двигает END date.
        // F36 правка: planned_* ВСЕГДА editable (план двигается всю жизнь проекта).
        // baseline_* — только при baseline_locked=false (замок открыт = идёт планирование).
        // Это совпадает с backend allowed_fields в tasks.py.
        const n = parseInt(state.newValue, 10);
        if (!Number.isFinite(n) || n <= 0) {
          showNotification('Дни должны быть > 0', 'error');
          rollback(state.columnName);
          return;
        }
        const isBaseline = state.columnName === 'baselineDuration';
        if (isBaseline) {
          const rawTask = _allTasks.find(rt => rt.id === state.id);
          const proj    = rawTask ? _allProjects.find(p => p.id === rawTask.project_id) : null;
          if (proj?.baseline_locked) {
            showNotification(
              'Замочек закрыт. Откройте 🔓 чтобы править Дни (Баз.).',
              'error'
            );
            rollback(state.columnName);
            return;
          }
        }
        const startStr = isBaseline ? task.baselineStart : task.plannedStart;
        if (!startStr) {
          showNotification(
            isBaseline ? 'Сначала задайте Баз.Начало' : 'Сначала задайте Начало',
            'error'
          );
          rollback(state.columnName);
          return;
        }
        const startDate = new Date(startStr);
        if (isNaN(startDate.getTime())) {
          showNotification('Невалидная стартовая дата', 'error');
          rollback(state.columnName);
          return;
        }
        const endDate = new Date(startDate.getTime() + (n - 1) * 86400000);
        const endStr  = formatDate(endDate);
        body = { [isBaseline ? 'baseline_end_date' : 'planned_end_date']: endStr };
      } else if (state.columnName === 'resource') {
        const id = parseInt(state.newValue, 10);
        if (!Number.isFinite(id) || id <= 0) {
          showNotification('Выберите ресурс из списка', 'error');
          rollback('owner_resource_id');
          return;
        }
        body = { owner_resource_id: id };
        const r = _resourcesCache.find(x => x.id === id);
        if (r) task.resource = r.name;
      } else if (state.columnName === 'minutes') {
        const n = parseInt(state.newValue, 10);
        if (!Number.isFinite(n) || n < 0) {
          showNotification('Минуты должны быть ≥ 0', 'error');
          rollback('minutes');
          return;
        }
        body = { ruslan_minutes: n };
      } else if (state.columnName === 'faktic') {
        const n = parseInt(state.newValue, 10);
        if (!Number.isFinite(n) || n < 0) {
          showNotification('Факт должен быть ≥ 0', 'error');
          rollback('faktic');
          return;
        }
        body = { actual_minutes_spent: n };
      } else if (state.columnName === 'volume_total') {
        // Inline-edit «Объём общ» = cost_rub (Руслан 2026-05-10).
        // Объём факт + в работе автоматически из cost_rub × work_pct/100.
        // Запрещён для милстоунов — у вехи нет «объёма».
        const rt1 = _allTasks.find(rt => rt.id === state.id);
        if (rt1?.is_milestone) {
          showNotification('У вехи нет объёма — это точка фиксации', 'error');
          rollback('volume_total');
          return;
        }
        const val = parseMoney(state.newValue);
        if (val !== null && (!Number.isFinite(val) || val < 0)) {
          showNotification('Сумма должна быть ≥ 0 (можно «10к», «1м»)', 'error');
          rollback('volume_total');
          return;
        }
        body = { cost_rub: val };
      } else if (state.columnName === 'progress') {
        // Inline-edit % выполнения (work_pct, миграция 016). Целое 0..100.
        // Для milestone запрещено — work_pct там пересчитывается БД-триггером
        // как AVG(child.work_pct), ручной ввод не имеет смысла.
        const rt2 = _allTasks.find(rt => rt.id === state.id);
        if (rt2?.is_milestone) {
          showNotification('% вехи считается автоматически от детей', 'error');
          rollback('progress');
          return;
        }
        const raw = String(state.newValue ?? '').trim();
        let val = null;
        if (raw !== '') {
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n) || n < 0 || n > 100) {
            showNotification('% должен быть 0..100', 'error');
            rollback('progress');
            return;
          }
          val = n;
        }
        body = { work_pct: val };
      } else if (state.columnName === 'calc_type') {
        // Sub-plan D: тип CCPM-расчёта. Пустое значение ('—' option) → NULL.
        // Backend recalc по типу — backlog (сейчас просто хранение).
        const v = String(state.newValue ?? '').trim();
        const allowed = new Set(['', 'fixed_duration', 'fixed_work', 'fixed_scope']);
        if (!allowed.has(v)) {
          showNotification('Неизвестный тип задачи', 'error');
          rollback('calc_type');
          return;
        }
        body = { calc_type: v === '' ? null : v };
      } else if (state.columnName === 'expense_total' || state.columnName === 'expense_actual') {
        // Sub-plan B5: inline-edit затрат через money-editor (parseMoney).
        // expense_planned (общ) запрещён под замочком — backend вернёт 409.
        // expense_actual (факт) разрешён всегда (он в allowed_fields).
        const val = parseMoney(state.newValue);
        if (val !== null && (!Number.isFinite(val) || val < 0)) {
          showNotification('Сумма должна быть ≥ 0 (можно «10к», «1м»)', 'error');
          rollback(state.columnName);
          return;
        }
        const dbField = state.columnName === 'expense_total'
          ? 'expense_planned_rub'
          : 'expense_actual_rub';
        body = { [dbField]: val };
      } else if (state.columnName === 'notes') {
        body = { notes: String(state.newValue ?? '').trim() || null };
      } else if (state.columnName === 'status') {
        // Mirror context-menu actions so started_at/completed_at/block_reason
        // are written by the backend (calibration data, principle №8).
        const newSt = String(state.newValue);
        const oldSt = String(state.oldValue);
        const onErr = () => rollback('status');
        if (newSt === 'in_progress' && oldSt !== 'in_progress') {
          await runTaskAction(state.id, () => api.startTask(state.id), 'Задача начата').catch(onErr);
          return;
        }
        if (newSt === 'done' && oldSt !== 'done') {
          await runTaskAction(state.id, () => api.completeTask(state.id, {}), 'Задача завершена').catch(onErr);
          return;
        }
        if (newSt === 'blocked' && oldSt !== 'blocked') {
          // Reason нужен — откатываем, открываем диалог как из ПКМ.
          rollback('status');
          openBlockDialog(state.id, task.text);
          return;
        }
        if (newSt === 'todo' && oldSt === 'blocked') {
          await runTaskAction(state.id, () => api.unblockTask(state.id), 'Разблокировано').catch(onErr);
          return;
        }
        // Reverts (done→todo, in_progress→todo) и cancelled — простой PATCH.
        body = { status: newSt };
      } else if (state.columnName === 'predecessors' || state.columnName === 'successors') {
        await syncDepsFromText(state, task);
        return;
      } else if (
        state.columnName === 'baselineStart' || state.columnName === 'baselineEnd' ||
        state.columnName === 'plannedStart'  || state.columnName === 'plannedEnd'
      ) {
        // F30+F36 (2026-05-10): inline-edit date-колонок.
        // F36 правка: planned_* ВСЕГДА editable (план двигается всю жизнь проекта).
        // baseline_* — только при baseline_locked=false (замок открыт).
        // Это совпадает с backend allowed_fields в tasks.py.
        const rawTask = _allTasks.find(rt => rt.id === state.id);
        const proj    = rawTask ? _allProjects.find(p => p.id === rawTask.project_id) : null;
        const isBaseline = state.columnName.startsWith('baseline');
        if (isBaseline && proj?.baseline_locked) {
          showNotification(
            'Замочек закрыт. Откройте 🔓 чтобы править базовые даты.',
            'error'
          );
          rollback(state.columnName);
          return;
        }
        // N1 (Руслан 2026-05-10): У вехи нет «Начала» (M2: колонки Баз.начало
        // и Начало пустые в шаблоне). Запрещаем inline-edit этих ячеек тоже —
        // иначе при клике в пустую клетку editor открывается с prefill из
        // task.baselineStart (где у вехи лежит fallback на deadline) и save
        // отправляет на backend данные, которые не должны существовать.
        if (rawTask?.is_milestone && (state.columnName === 'baselineStart' || state.columnName === 'plannedStart')) {
          showNotification('У вехи нет «Начала» — это точка фиксации. Меняйте «Окончание» / «Баз. окончание».', 'error');
          rollback(state.columnName);
          return;
        }
        // DHTMLX 8.x inline editor type='date' возвращает в state.newValue
        // объект Date (а не строку). Также принимаем строки DD.MM.YYYY и
        // YYYY-MM-DD на случай ручного ввода/других браузеров.
        let dateStr = '';
        if (state.newValue instanceof Date) {
          const d = state.newValue;
          if (!isNaN(d.getTime())) {
            const yyyy = d.getFullYear();
            const mm   = String(d.getMonth() + 1).padStart(2, '0');
            const dd   = String(d.getDate()).padStart(2, '0');
            dateStr = `${yyyy}-${mm}-${dd}`;
          }
        } else {
          dateStr = String(state.newValue ?? '').trim();
          const ddmm = dateStr.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/);
          if (ddmm) {
            const [, d, m, y] = ddmm;
            dateStr = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
          }
        }
        if (dateStr && !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          showNotification('Дата должна быть в формате DD.MM.YYYY', 'error');
          rollback(state.columnName);
          return;
        }
        const dbField = {
          baselineStart: 'baseline_start_date',
          baselineEnd:   'baseline_end_date',
          plannedStart:  'planned_start_date',
          plannedEnd:    'planned_end_date',
        }[state.columnName];
        body = { [dbField]: dateStr || null };
      } else if (state.columnName === 'parent_task_id') {
        // F16 (2026-05-10): inline-edit колонки Main = parent_task_id.
        // Пустое значение → null (вынести из иерархии). Иначе число.
        // Backend trigger validate_milestone_parent (миграция 034) проверит
        // существование parent + 2-level depth.
        const raw = String(state.newValue ?? '').trim();
        if (raw === '') {
          body = { parent_task_id: null };
        } else {
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n) || n <= 0) {
            showNotification('Main должен быть числом — id родительской задачи', 'error');
            rollback('parent_task_id');
            return;
          }
          if (n === state.id) {
            showNotification('Задача не может быть родителем самой себе', 'error');
            rollback('parent_task_id');
            return;
          }
          body = { parent_task_id: n };
        }
      } else {
        return;
      }

      showSaving();
      try {
        // Поля из RECALC_TRIGGER_FIELDS (plan/baseline-даты, длительности,
        // calc_type, parent_task_id) запускают backend recalc по dep_type —
        // patchTaskAndRefetchIfRecalc делает PATCH + полный рефетч + rerender
        // grid'а. Для прочих полей (notes, status, money) идёт чистый PATCH +
        // локальная синхронизация bar'а.
        const willRefetch = Object.keys(body).some(k => RECALC_TRIGGER_FIELDS.has(k));
        await patchTaskAndRefetchIfRecalc(state.id, body);
        if (!willRefetch) {
          // Прогресс-бар на самой Gantt-диаграмме читает task.progress (0..1),
          // а editor пишет в task.work_pct (0..100). Синхронизируем, чтобы бар
          // обновился сразу, а не на следующем polling-fetch.
          if (body.work_pct !== undefined) {
            task.progress = (body.work_pct ?? 0) / 100;
          }
          gantt.silent(() => gantt.updateTask(state.id));
        }
        showNotification('Сохранено', 'success');
      } catch (err) {
        const fieldByCol = {
          text: 'text', duration: 'duration', resource: 'resource',
          minutes: 'minutes', faktic: 'faktic', notes: 'notes', status: 'status',
          parent_task_id: 'parent_task_id',
          // F30: rollback для date-колонок при PATCH-ошибке от backend.
          baselineStart: 'baselineStart', baselineEnd: 'baselineEnd',
          plannedStart:  'plannedStart',  plannedEnd:  'plannedEnd',
          // F33: rollback для дни-колонок.
          baselineDuration: 'baselineDuration',
        };
        const f = fieldByCol[state.columnName];
        if (f) task[f] = state.oldValue;
        gantt.silent(() => gantt.updateTask(state.id));
        const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка сохранения';
        showNotification(typeof msg === 'string' ? msg : 'Ошибка сохранения', 'error');
      } finally {
        hideSaving();
      }
    });
  }

  // ── Right-click context menu (Phase D) ────────────────────────────────────
  gantt.attachEvent('onContextMenu', (taskId, _linkId, e) => {
    if (taskId == null) return true; // empty area or link → native menu
    if (!gantt.isTaskExists(taskId)) return true;
    const task = gantt.getTask(taskId);
    if (!isEditableTask(task)) return true;

    if (e?.preventDefault) e.preventDefault();
    showContextMenu(e.clientX, e.clientY, buildTaskMenu(task));
    return false;
  });

  // Left click → open inspector. Project-bar → project-inspector;
  // editable task → task-inspector; buffer/other → закрыть оба.
  gantt.attachEvent('onTaskClick', (id) => {
    hideContextMenu();
    if (!gantt.isTaskExists(id)) return true;
    const task = gantt.getTask(id);

    // F3 fix (2026-05-10): whitelist по ПРЕФИКСУ id, не по task.type.
    // После Phase D любая задача с children получает type=project (DHTMLX
    // project-row), но id числовой. Старый код брал task.type === project →
    // фазы/сводные попадали в ветку selectProject и инспектор не открывался.
    //
    // Префиксы id:
    //   - 'p_NNN'    → project summary bar
    //   - 'buf_N'    → project buffer (computed visual, не editable)
    //   - 'fbuf_NN'  → feeding buffer (computed visual, не editable)
    //   - число      → реальная задача (включая фазы/сводные/вехи) → task-inspector
    const idStr = String(id);
    if (idStr.startsWith('p_')) {
      if (_selectedTaskId !== null) selectTask(null);
      const projectId = Number(idStr.slice(2));
      if (!Number.isFinite(projectId)) return true;
      selectProject(projectId);
      return true;
    }
    if (idStr.startsWith('buf_') || idStr.startsWith('fbuf_')) {
      // Computed visual без БД-id → инспектор не открываем.
      if (_selectedTaskId !== null)    selectTask(null);
      if (_selectedProjectId !== null) selectProject(null);
      return true;
    }
    // Реальная задача (number id) — открываем task-inspector. Включает фазы
    // и сводные (которые тоже имеют type=project, но id числовой).
    if (!isEditableTask(task) && !task._isPhase && !task._isSummary && !task._isMilestone) {
      if (_selectedTaskId !== null)    selectTask(null);
      if (_selectedProjectId !== null) selectProject(null);
      return true;
    }
    if (_selectedProjectId !== null) selectProject(null);
    selectTask(id);
    return true;
  });

  // Click on empty timeline → drop selection so the inspector closes.
  gantt.attachEvent('onEmptyClick', () => {
    hideContextMenu();
    if (_selectedTaskId !== null)    selectTask(null);
    if (_selectedProjectId !== null) selectProject(null);
    return true;
  });

  // ── Vertical reorder (Phase 5) ────────────────────────────────────────────
  // DHTMLX has already moved the row visually by the time onRowDragEnd fires;
  // we just need to persist the new order under the same project parent.
  gantt.attachEvent('onRowDragEnd', async (id /* , target */) => {
    if (!gantt.isTaskExists(id)) return;
    const task = gantt.getTask(id);
    if (!isEditableTask(task)) return;
    const parent = task.parent;
    const siblings = gantt.getChildren(parent)
      .map(cid => gantt.getTask(cid))
      .filter(t => isEditableTask(t));
    // Normalize id to number — DHTMLX 8.x stores some task ids as strings,
    // and `_allTasks.find(t => t.id === it.id)` below relies on strict equality
    // against numeric ids from the API.
    const items = siblings.map((t, idx) => ({
      id: typeof t.id === 'string' && /^\d+$/.test(t.id) ? Number(t.id) : t.id,
      sort_order: idx + 1,
    }));
    if (!items.length) return;

    showSaving();
    try {
      await api.reorderTasks(items);
      // Mirror new sort_order into _allTasks so any subsequent rerenderGantt()
      // (e.g. filter change) keeps the ordering instead of snapping back to
      // sort_order = id.
      for (const it of items) {
        const t = _allTasks.find(t => t.id === it.id);
        if (t) t.sort_order = it.sort_order;
      }
      showNotification('Порядок сохранён', 'success');
    } catch (err) {
      // Roll back to server truth — refetch + redraw so the user sees the
      // pre-drag order, not a divergent local state.
      _allTasks = await api.tasks();
      rerenderGantt();
      const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Не удалось сохранить порядок';
      showNotification(typeof msg === 'string' ? msg : 'Не удалось сохранить порядок', 'error');
      console.error('Reorder error:', err);
    } finally {
      hideSaving();
    }
  });
}

// ── Inspector selection (Phase 1) ───────────────────────────────────────────

function selectTask(id) {
  // DHTMLX 8.x emits ids as strings from onTaskClick even for numeric tasks,
  // while _allTasks (from API) holds numeric ids. Coerce so strict equality
  // works against _allTasks and against _selectedTaskId on subsequent clicks.
  if (typeof id === 'string' && /^\d+$/.test(id)) id = Number(id);
  // Защита от попадания pseudo-id `p_NNN` (project-bar) или `buf_NNN` (buffer)
  // в task-инспектор. Корректные task-id всегда числовые после coerce выше.
  // Если попало нечисло — это баг в caller'е; не открываем инспектор и логируем.
  if (id != null && typeof id !== 'number') {
    console.error('[selectTask] non-numeric id, ignored:', id);
    return;
  }
  const prev = _selectedTaskId;
  if (prev === id) return;
  _selectedTaskId = id;
  if (prev != null && gantt.isTaskExists(prev)) gantt.refreshTask(prev);
  if (id   != null && gantt.isTaskExists(id))   gantt.refreshTask(id);

  if (id == null) {
    closeTaskInspector();
    return;
  }
  const fullTask = _allTasks.find(t => t.id === id);
  if (!fullTask) return;
  openTaskInspector({
    task:       fullTask,
    resources:  _resourcesCache,
    projects:   _allProjects,
    // F5 (2026-05-10): передаём ВСЕ задачи (не только milestones).
    // milestonesForProject в task-inspector сам сортирует milestones first.
    milestones: _allTasks,
    onAction:   (action) => handleInspectorAction(fullTask, action),
    onClose:    () => {
      const closed = _selectedTaskId;
      _selectedTaskId = null;
      if (closed != null && gantt.isTaskExists(closed)) gantt.refreshTask(closed);
    },
  });
}

function handleInspectorAction(task, action) {
  if (action === 'save')     return saveInspectorTask(task);
  if (action === 'complete') return openCompleteDialog(task.id, task.name);
  if (action === 'start')    return runTaskAction(task.id, () => api.startTask(task.id), 'Задача начата');
  if (action === 'unblock')  return runTaskAction(task.id, () => api.unblockTask(task.id), 'Разблокировано');
  if (action === 'delete')   return openDeleteDialog(task.id, task.name);
  if (action === 'block')    return openBlockDialog(task.id, task.name);
  // F20 (2026-05-10): toggle is_milestone в инспекторе.
  if (action === 'convert-to-task')      return convertMilestoneToTask(task);
  if (action === 'convert-to-milestone') return convertTaskToMilestone(task);
}

async function convertMilestoneToTask(task) {
  const ok = window.confirm(
    `Преобразовать веху «${task.name}» в обычную задачу?\n\n` +
    'Длительность станет 1 день (можно изменить в инспекторе). ' +
    'Связи-стрелки и parent_task_id сохранятся.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    // F21 (2026-05-10): через атомарный endpoint /to-task. Backend сам
    // обнуляет deadline+deadline_type, выставляет duration_days=max(1,
    // current). Без этого CHECK constraint chk_milestone_fields бросает
    // 500 на простом PATCH is_milestone=false.
    await api.convertToTask(task.id);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    showNotification('Веха → задача', 'success');
    selectTask(task.id);
  } catch (err) {
    const detail = err?.detail ?? err?.message ?? err;
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    showNotification(`Ошибка преобразования: ${text}`, 'error');
  } finally {
    hideSaving();
  }
}

async function convertTaskToMilestone(task) {
  const ok = window.confirm(
    `Преобразовать задачу «${task.name}» в веху?\n\n` +
    'Длительность станет 0. ruslan_minutes тоже обнулится. Дедлайн возьмётся ' +
    'из текущей planned_end_date (или planned_start_date). Связи и ' +
    'parent_task_id сохранятся.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    // F21: атомарный endpoint /to-milestone. Backend выставляет
    // is_milestone=TRUE, duration=0, ruslan_minutes=0, deadline=
    // planned_end_date, deadline_type='soft'. Если planned_end_date
    // отсутствует — backend бросит 422 с понятной ошибкой.
    await api.convertToMilestone(task.id);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    showNotification('Задача → веха', 'success');
    selectTask(task.id);
  } catch (err) {
    const detail = err?.detail ?? err?.message ?? err;
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    showNotification(`Ошибка преобразования: ${text}`, 'error');
  } finally {
    hideSaving();
  }
}

async function onUnlockBaselineClick(projectId) {
  // Снятие baseline — admin-операция для replan-сценария: пользователь
  // хочет переструктурировать (поменять parent_task_id, удалить вехи,
  // изменить deadline). Под `baseline_locked_at IS NOT NULL` backend
  // рубит структурные правки 409. После snять → переструктурировать →
  // снова 🔒 Зафиксировать или 🔄 Пересчитать.
  const ok = window.confirm(
    'Снять baseline-замок с проекта?\n\n' +
    'После этого можно будет менять структуру: «Дни», «Минуты», «Бюджет», ' +
    '«Дедлайн вехи», «Родительская веха», и т.п. Сами baseline-значения ' +
    '(Баз. начало / Баз. окончание / Дни (баз.)) сохранятся как историческая ' +
    'информация — снимется только защита.\n\n' +
    'После переструктурирования нажми 🔒 Зафиксировать заново, чтобы ' +
    'обновлённый план стал новым эталоном план/факта.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    const result = await api.unlockBaseline(projectId);
    const n = Number(result?.unlocked_count ?? 0);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    alert(n > 0
      ? `Замок снят с ${n} задач${n === 1 ? 'и' : ''}. Теперь можно менять структуру.`
      : 'Все задачи проекта уже без замка.');
  } catch (err) {
    alert(`Ошибка снятия baseline: ${err?.message ?? err}`);
  } finally {
    hideSaving();
  }
}

async function onRebaselineClick(projectId) {
  // Жёсткий confirm — операция перезаписывает baseline для ВСЕХ задач проекта
  // (включая уже залоченные). Это нужно когда исходный план потерял смысл
  // из-за новых вводных (см. CCPM «replan» сценарий). История старого
  // baseline не сохраняется.
  const ok = window.confirm(
    'Пересчитать baseline для проекта?\n\n' +
    'Это перезапишет план («Базовое начало», «Базовое окончание», «Дни (баз.)», ' +
    '«Минуты (баз.)») у ВСЕХ задач — текущими расчётными значениями. Старый ' +
    'baseline будет утерян.\n\n' +
    'Использовать только если исходный план потерял смысл из-за внешних ' +
    'изменений (новые задачи, сдвинутый дедлайн, и т.п.). По CCPM это редкая ' +
    'операция — обычно baseline должен оставаться как «эталон» для калибровки оценок.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    const result = await api.rebaselineProject(projectId);
    const n = Number(result?.updated_count ?? 0);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    alert(`Baseline пересчитан: ${n} задач обновлено.`);
  } catch (err) {
    alert(`Ошибка пересчёта baseline: ${err?.message ?? err}`);
  } finally {
    hideSaving();
  }
}

// Phase E (план 2026-05-09): project-level замочек baseline.
// Phase 2 baseline-edit-mode (2026-05-11): при unlock переключаем .gantt_container
// в data-edit-mode="baseline" — current уходит в призрак, baseline становится
// ярким основным планом. При закрытии замка — снимаем атрибут.
async function onBaselineLockToggle(projectId) {
  try {
    // Перед toggle — закрыть inline-editors (DHTMLX 8.x), иначе blur
    // конкурирует с PATCH и затирает значение.
    try { gantt.ext?.inlineEditors?.save?.(); } catch { /* no editor open */ }
    showSaving();
    const proj = await api.toggleBaselineLock(projectId);
    const locked = !!proj.baseline_locked;
    // F1+F7 (2026-05-10): меняем SVG-иконку замка по состоянию (закрыт/открыт).
    // currentColor = teal-bright из CSS .baseline-lock-toggle.
    const lockBtn = document.querySelector('.baseline-lock-toggle');
    if (lockBtn) {
      lockBtn.innerHTML = iconLock(!locked, 14);
      lockBtn.classList.toggle('is-locked', locked);
      lockBtn.classList.toggle('is-unlocked', !locked);
    }
    // Phase 2: data-edit-mode на .gantt_container + baseline-group.
    // CSS [data-edit-mode="baseline"] переключает opacity .gantt_task_line,
    // яркость .gantt-baseline-bar, видимость бейджа и чекбокса.
    const ganttRootEl = document.querySelector('.gantt_container');
    const baseGroupEl = document.querySelector('[data-baseline-group]');
    if (!locked) {
      if (ganttRootEl) ganttRootEl.dataset.editMode = 'baseline';
      if (baseGroupEl) baseGroupEl.dataset.editMode = 'baseline';
      // Восстановить состояние чекбокса «Current» из localStorage.
      // OFF = current скрыт через [data-current-hidden] CSS.
      if (ganttRootEl && !_showCurrentInBaselineMode) {
        ganttRootEl.dataset.currentHidden = 'true';
      }
    } else {
      if (ganttRootEl) {
        delete ganttRootEl.dataset.editMode;
        delete ganttRootEl.dataset.currentHidden;
      }
      if (baseGroupEl) delete baseGroupEl.dataset.editMode;
    }
    // Refetch projects чтобы новое состояние было видно в фильтрах/инспекторе
    _allProjects = await api.projects();
    rerenderGantt();
    showNotification(
      locked
        ? 'Замочек baseline закрыт — даты заморожены'
        : 'Замочек baseline открыт — теперь можно править эталон',
      'success',
    );
    // Phase 4 «baseline edit mode» (план 2026-05-11): при закрытии замка
    // (locked === true) предлагаем пересчитать planned по связям. Эталон
    // изменился — текущий план мог стать рассинхронизированным с baseline-
    // каскадом. Recalculate_planned_dates умный — учитывает dep_type/lag,
    // фильтрует status='todo' AND NOT is_milestone (done/in_progress остаются).
    if (locked) {
      const shouldRecalc = await openModal({
        title: 'Пересчитать текущий план?',
        message:
          'Эталон обновлён. Пересчитать planned-даты по связям (FS/SS/FF + lag)? '
          + 'Пересчёт затронет только todo-задачи (без вех); done и in_progress останутся как есть.',
        confirmLabel: 'Пересчитать',
        cancelLabel: 'Не сейчас',
      });
      if (shouldRecalc) {
        try {
          showSaving();
          const result = await api.recalculatePlanned(projectId);
          _allTasks = await api.tasks(_taskFetchParams);
          rerenderGantt();
          const n = Number(result?.updated_count ?? 0);
          if (n > 0) {
            showNotification(`Текущий план пересчитан: ${n} задач`, 'success');
          } else {
            showNotification('Пересчёт пропущен — нет todo-задач для пересчёта', 'info');
          }
        } catch (err) {
          const detail = err?.detail ?? err?.message ?? err;
          const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
          showNotification(`Ошибка пересчёта плана: ${text}`, 'error');
        } finally {
          hideSaving();
        }
      }
    }
  } catch (err) {
    showNotification(`Ошибка toggle замочка: ${err?.message ?? err}`, 'error');
  } finally {
    hideSaving();
  }
}

// Phase E: пересчёт baseline_*_date по CPM. Требует открытый замочек.
async function onRecalcBaselineClick(projectId) {
  const ok = window.confirm(
    'Пересчитать baseline для проекта?\n\n' +
    'Это перепишет baseline-даты ВСЕХ задач проекта по текущему CPM (FS/SS/FF + длительность). ' +
    'Замочек должен быть открыт — иначе сервер вернёт 409.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    const result = await api.recalculateBaseline(projectId);
    const n = Number(result?.updated_count ?? 0);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    showNotification(`Baseline пересчитан: ${n} задач${n === 1 ? 'а' : ''}`, 'success');
  } catch (err) {
    const detail = err?.detail ?? err?.message ?? err;
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    showNotification(`Ошибка пересчёта baseline: ${text}`, 'error');
  } finally {
    hideSaving();
  }
}

// F12 (2026-05-10): явный пересчёт текущего плана (planned_*_date по CPM).
// /recalculate endpoint существует — backend `_recalculate()` зовёт
// recalculate_planned_dates() + apply_critical_path() + auto_create_project_buffer().
// Доступно всегда, замочек не требуется (текущий план — не baseline).
async function onRecalcPlannedClick(projectId) {
  try {
    showSaving();
    await api.recalculatePlanned(projectId);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    showNotification('Текущий план пересчитан', 'success');
  } catch (err) {
    // F35 (2026-05-10): на больших проектах (100+ задач, 100+ deps) backend
    // calculate_critical_path → 504 Gateway Timeout (nginx 30s limit).
    // Конкретное сообщение помогает понять что это backend perf issue, не фейл
    // логики. Долгосрочно — оптимизация SQL функции (F36, отдельная задача).
    const status = err?.status ?? err?.response?.status;
    const isTimeout = status === 504 || /timeout|gateway/i.test(String(err?.message ?? err));
    if (isTimeout) {
      showNotification(
        'Backend recalc не успел за 30с — слишком много задач/связей. ' +
        'Нужна оптимизация SQL (см. F36).',
        'error'
      );
    } else {
      const detail = err?.detail ?? err?.message ?? err;
      const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
      showNotification(`Ошибка пересчёта плана: ${text}`, 'error');
    }
  } finally {
    hideSaving();
  }
}

// Phase 3 (план baseline-edit-mode, 2026-05-11): «Привести к базовому».
// Копирует baseline_*_date → planned_*_date для ВСЕХ задач проекта,
// включая done/in_progress/cancelled. Длительность и минуты тоже переписываются.
// Бюджет, расходы, факт выполнения — НЕ трогаются. Необратимо.
async function onResetToBaselineClick(projectId) {
  try {
    // Считаем breakdown по статусу из локального кэша задач (отдельный GET
    // не нужен — _allTasks свежий после последнего rerender).
    const projTasks = (_allTasks ?? []).filter(t => t.project_id === projectId);
    const breakdown = projTasks.reduce((acc, t) => {
      const st = t.status ?? 'todo';
      acc[st] = (acc[st] ?? 0) + 1;
      return acc;
    }, {});
    const parts = [];
    if (breakdown.todo)        parts.push(`${breakdown.todo} todo`);
    if (breakdown.in_progress) parts.push(`${breakdown.in_progress} in_progress`);
    if (breakdown.done)        parts.push(`${breakdown.done} done`);
    if (breakdown.cancelled)   parts.push(`${breakdown.cancelled} cancelled`);
    const breakdownStr = parts.length ? parts.join(' + ') : 'все задачи';

    const confirmed = await openModal({
      title: 'Привести план к базовому?',
      message:
        `${breakdownStr} будут переписаны под baseline. ` +
        'Переписываются: даты, длительность, минуты. ' +
        'НЕ переписываются: бюджет, расходы, факт выполнения. ' +
        'Отмена невозможна.',
      confirmLabel: 'Привести',
      cancelLabel: 'Отмена',
      danger: true,
    });
    if (!confirmed) return;

    showSaving();
    const result = await api.resetToBaseline(projectId);
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    const n = Number(result?.updated_count ?? 0);
    showNotification(`План синхронизирован с baseline: ${n} задач`, 'success');
  } catch (err) {
    const detail = err?.detail ?? err?.message ?? err;
    const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
    showNotification(`Ошибка «привести к базовому»: ${text}`, 'error');
  } finally {
    hideSaving();
  }
}

async function onLockBaselineClick(projectId) {
  // Confirm-диалог — фиксация baseline необратима без админ-разлока: после неё
  // нельзя менять «Дни», «Минуты», «Бюджет», «Дедлайн вехи» (см. update_task
  // в backend/app/api/tasks.py — allowed_fields под baseline_locked_at).
  const ok = window.confirm(
    'Зафиксировать baseline для проекта?\n\n' +
    'После фиксации нельзя будет менять «Дни», «Минуты», «Бюджет», «Дедлайн вехи» ' +
    'без админ-разлока. Незавершённые задачи всё ещё смогут двигаться по датам ' +
    '(planned_*) и менять «Сделано» / «Заметки».\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  try {
    showSaving();
    const result = await api.lockBaseline(projectId);
    const n = Number(result?.locked_count ?? 0);
    // Re-fetch чтобы baseline-колонки заполнились свежими значениями.
    _allTasks = await api.tasks(_taskFetchParams);
    rerenderGantt();
    alert(n > 0
      ? `Baseline зафиксирован для ${n} задач${n === 1 ? 'и' : ''}.`
      : 'Все задачи проекта уже залочены — нечего фиксировать.');
  } catch (err) {
    alert(`Ошибка фиксации baseline: ${err?.message ?? err}`);
  } finally {
    hideSaving();
  }
}

function openNewTaskInspector() {
  // Drop existing selection visual so the highlight doesn't linger on the previous task.
  if (_selectedTaskId !== null) selectTask(null);
  // Default project: when user is filtered down to a single project, preselect it.
  const defaultProjectId = _selectedIds.size === 1 ? [..._selectedIds][0] : null;
  openCreateTaskInspector({
    projects:         _allProjects,
    resources:        _resourcesCache,
    milestones:       _allTasks.filter(t => t.is_milestone),
    defaultProjectId,
    onAction: async (action) => {
      if (action === 'cancel') return closeTaskInspector();
      if (action === 'create') return handleCreate();
    },
    onClose: () => {},
  });
}

async function handleCreate() {
  const payload = getInspectorCreatePayload();
  if (!payload) return; // validation already surfaced inline
  showSaving();
  try {
    const created = await api.createTask(payload);
    // Refetch instead of just pushing `created` — DB triggers may set derived
    // fields (planned_start_date, schedule slot, …), and computeSchedule
    // silently drops tasks lacking dates.
    _allTasks = await api.tasks();
    rerenderGantt();
    selectTask(created.id);
    // DHTMLX virtual-scrolls long lists — scroll the new row into view so the
    // selection highlight is visible without the user hunting for it.
    if (gantt.isTaskExists(created.id)) gantt.showTask(created.id);
    showNotification('Задача создана', 'success');
  } catch (err) {
    const detail = err.detail;
    const msg = detail?.message ?? detail ?? err.message ?? 'Ошибка создания';
    const text = typeof msg === 'string' ? msg : 'Ошибка создания';
    showInspectorError(text);
    showNotification(text, 'error');
    console.error('Inspector create error:', err);
  } finally {
    hideSaving();
  }
}

async function saveInspectorTask(task) {
  const diff = getInspectorDiff();
  if (!diff) return;
  // Defensive: task.id должен быть числом (см. selectTask coerce). Если сюда
  // попадёт `p_NNN` — будет 5+ ошибок «Task not found id=p_NN» в polling-цикле.
  if (typeof task.id !== 'number') {
    console.error('[saveInspectorTask] non-numeric id, aborting:', task.id);
    showNotification('Внутренняя ошибка: некорректный id задачи', 'error');
    return;
  }
  showSaving();
  try {
    await api.patchTask(task.id, diff);
    // Read-after-write: DB triggers recompute is_on_critical_chain (and possibly
    // other derived fields) AFTER our update returns, so refetch to render the
    // truth. PATCH alone returns the row mid-trigger and would diverge from DB.
    const fresh = await api.getTask(task.id);
    refreshTaskInGantt(fresh);
    // Schedule recompute in case dates / duration / resource changed.
    rerenderGantt();
    showNotification('Сохранено', 'success');
  } catch (err) {
    const detail = err.detail;
    const baselineLocked =
      err.status === 409 &&
      typeof detail === 'string' &&
      detail.startsWith('Baseline locked');
    if (baselineLocked) {
      showInspectorError(detail);
      showNotification('Baseline залочен — часть полей нельзя править', 'error');
      return;
    }
    const msg = detail?.message ?? detail ?? err.message ?? 'Ошибка сохранения';
    showNotification(typeof msg === 'string' ? msg : 'Ошибка сохранения', 'error');
    console.error('Inspector save error:', err);
  } finally {
    hideSaving();
  }
}

// ── Project inspector wiring ─────────────────────────────────────────────────

function selectProject(id) {
  if (typeof id === 'string' && /^\d+$/.test(id)) id = Number(id);
  const prev = _selectedProjectId;
  if (prev === id) return;
  _selectedProjectId = id;
  if (id == null) {
    closeProjectInspector();
    return;
  }
  const fullProject = _allProjects.find(p => p.id === id);
  if (!fullProject) return;
  openProjectInspector({
    project:  fullProject,
    onAction: (action) => handleProjectInspectorAction(fullProject, action),
    onClose:  () => { _selectedProjectId = null; },
  });
}

function handleProjectInspectorAction(project, action) {
  if (action === 'save')     return saveInspectorProject(project);
  if (action === 'activate') return openActivateDialog(project);
  if (action === 'pause')    return openPauseDialog(project);
  if (action === 'resume')   return runProjectAction(project.id, () => api.resumeProject(project.id), 'Проект возобновлён');
  if (action === 'complete') return openProjectCompleteDialog(project);
  if (action === 'cancel')   return openProjectCancelDialog(project);
}

function openNewProjectInspector() {
  if (_selectedTaskId !== null)    selectTask(null);
  if (_selectedProjectId !== null) selectProject(null);
  openCreateProjectInspector({
    onAction: async (action) => {
      if (action === 'cancel')      return closeProjectInspector();
      if (action === 'create')      return handleCreateProject();
      if (action === 'build-gantt') return handleBuildGanttFromDocs();
    },
    onClose: () => {},
  });
}

async function handleCreateProject() {
  const payload = getProjectInspectorCreatePayload();
  if (!payload) return;
  showSaving();
  try {
    const created = await api.createProject(payload);
    // Refetch projects+tasks: триггеры могут что-то посчитать; ширина multi-select
    // и список проектов в task-create form должны обновиться.
    _allProjects = await api.projects();
    // Auto-select новый проект, чтобы он сразу был виден на Gantt'е.
    _selectedIds.add(created.id);
    rerenderGantt();
    persistState();
    selectProject(created.id);
    showNotification('Проект создан', 'success');
  } catch (err) {
    const detail = err.detail;
    const msg = detail?.message ?? detail ?? err.message ?? 'Ошибка создания';
    const text = typeof msg === 'string' ? msg : 'Ошибка создания';
    showProjectInspectorError(text);
    showNotification(text, 'error');
    console.error('Project create error:', err);
  } finally {
    hideSaving();
  }
}

/**
 * Flow для кнопки «📊 Сформировать Диаграмму Гантта» в create-режиме инспектора.
 *
 * Шаги:
 * 1. POST /api/import/parse — парсит загруженные docx, возвращает draft.
 * 2. Если draft.contract_deadline / charter поля конфликтуют с введёнными в
 *    форме — показать diff-диалог через openModal, дать выбрать источник.
 * 3. POST /api/import/instantiate — создаёт проект через assembler.
 * 4. Параллельно: загрузить все 5 docx в /api/projects/{id}/documents
 *    (хранение для будущего скачивания + истории).
 */
async function handleBuildGanttFromDocs() {
  const built = getProjectInspectorBuildPayload();
  if (!built) return;
  const { meta, files } = built;
  showSaving();
  try {
    // 1. Parse — собираем все File в массив для multipart upload
    const filesArray = Object.values(files);
    const draft = await api.parseImport(filesArray);
    if (draft.warnings && draft.warnings.length) {
      console.warn('Import warnings:', draft.warnings);
    }
    if (!draft.sections || draft.sections.length === 0) {
      throw new Error('Не распознано ни одной секции в загруженных файлах');
    }
    // 2026-05-14: подразделы (ИОС4.1, ТХ.1) после fallback на родителя — теперь
    // имеют section_template_id. Если всё-таки есть unmapped — показываем
    // confirm-modal вместо жёсткого throw'а: пользователь решает продолжить
    // или отменить (вместо «всё или ничего»).
    const unmapped = draft.sections.filter(s => s.section_template_id == null);
    if (unmapped.length) {
      const codes = unmapped.map(s => s.code).join(', ');
      const { openModal } = await import('../components/modal.js');
      const proceed = await openModal({
        title: 'Не все разделы распознаны',
        message:
          `В библиотеке не найдены: ${codes}.\n\n` +
          `Распознанные разделы (${draft.sections.length - unmapped.length}) будут импортированы. ` +
          `Нераспознанные — пропущены (добавите позже вручную или расширьте библиотеку).`,
        confirmLabel: 'Импортировать только распознанные',
      });
      if (!proceed) return;  // отмена
    }
    // Оставляем только секции с known template_id для assembler'а.
    const knownSections = draft.sections.filter(s => s.section_template_id != null);
    if (knownSections.length === 0) {
      throw new Error('Ни один раздел не распознан в библиотеке');
    }

    // 2. Форма подтверждения — показываем ВСЁ распознанное парсерами,
    //    пользователь проверяет и правит перед созданием проекта.
    const { openModal } = await import('../components/modal.js');
    const sectionCodes = knownSections.map(s => s.code).join(', ');
    const summary =
      `Распознано разделов: ${knownSections.length}` +
      (unmapped.length ? ` · не распознано: ${unmapped.length}` : '') +
      `\n${sectionCodes}`;
    // Имя проекта — колонка projects.name ограничена 100 символами.
    // Официальное имя из ЗНП обычно длиннее → предлагаем часть до первой
    // запятой, полное наименование сохраним в charter_md.
    let nameSuggestion = draft.name || meta.name || '';
    if (nameSuggestion.length > 100) {
      const short = nameSuggestion.split(',')[0].trim();
      nameSuggestion = (short.length >= 8 && short.length <= 100)
        ? short : nameSuggestion.slice(0, 100);
    }
    const confirmed = await openModal({
      title: 'Проверьте данные проекта',
      message: summary,
      fields: [
        { name: 'name', label: 'Название проекта *', type: 'text',
          value: nameSuggestion, required: true, maxlength: 100 },
        { name: 'goal', label: 'Цель проекта', type: 'text',
          value: meta.goal || '' },
        { name: 'customer', label: 'Заказчик', type: 'text',
          value: draft.customer || '' },
        { name: 'location', label: 'Адрес / локация', type: 'text',
          value: draft.location || '' },
        { name: 'construction_type', label: 'Вид строительства', type: 'text',
          value: draft.construction_type || '' },
        { name: 'funding', label: 'Финансирование', type: 'text',
          value: draft.funding || '' },
        { name: 'budget_total_rub', label: 'Бюджет, ₽ *', type: 'number',
          value: draft.contract_price_rub ?? '', required: true, min: 0 },
        { name: 'hard_deadline', label: 'Срок (deadline)', type: 'date',
          value: draft.contract_deadline || meta.hard_deadline || '' },
        { name: 'contract_number', label: '№ договора', type: 'text',
          value: draft.contract_number || '' },
        { name: 'contract_date', label: 'Дата договора', type: 'date',
          value: draft.contract_date || '' },
      ],
      confirmLabel: 'Создать проект',
    });
    if (!confirmed) return;  // отмена

    // 3. Instantiate с подтверждёнными значениями.
    //    Все секции — на одного исполнителя (первого в списке ресурсов).
    const resources = await api.resources();
    const defaultResId = resources?.[0]?.id;
    if (!defaultResId) throw new Error('В системе нет ресурсов — добавьте через /admin');
    // 2026-05-14: name прокидываем для подразделов (ИОС4.1 mapped на ИОС4 —
    // раздел-обёртка получит имя из Состава, не из шаблона).
    const sections = knownSections.map(s => ({
      section_template_id: s.section_template_id,
      responsible_resource_id: defaultResId,
      name: s.name || null,
    }));

    // charter_md — структурных колонок под заказчика/договор в projects нет,
    // поэтому подтверждённые метаданные складываем в markdown-блок.
    const mdLines = [];
    for (const [lbl, val] of [
      ['Полное наименование',
        (draft.name && draft.name !== confirmed.name) ? draft.name : null],
      ['Заказчик', confirmed.customer],
      ['Адрес', confirmed.location],
      ['Вид строительства', confirmed.construction_type],
      ['Финансирование', confirmed.funding],
      ['№ договора', confirmed.contract_number],
      ['Дата договора', confirmed.contract_date],
    ]) {
      if (val) mdLines.push(`**${lbl}**: ${val}`);
    }
    const charterMd = mdLines.length ? mdLines.join('\n\n') : (draft.charter_md ?? null);

    const instantiateBody = {
      name: confirmed.name,
      goal: confirmed.goal ?? '',
      hard_deadline: confirmed.hard_deadline || null,
      priority: meta.priority,
      contour: meta.contour ?? null,
      portfolio_id: meta.portfolio_id ?? null,
      budget_total_rub: confirmed.budget_total_rub ?? 0,
      sections,
      charter_md: charterMd,
    };
    const result = await api.instantiateImport(instantiateBody);
    const projectId = result.project_id;

    // 4. Загружаем все 5 docx как attachments проекта (хранение).
    //    Делаем последовательно, чтобы 422 на одном файле не похоронил другие.
    for (const [docType, file] of Object.entries(files)) {
      try {
        await api.uploadProjectDocument(projectId, docType, file);
      } catch (err) {
        console.warn(`Не удалось загрузить ${docType}:`, err);
      }
    }

    _allProjects = await api.projects();
    _selectedIds.add(projectId);
    rerenderGantt();
    persistState();
    selectProject(projectId);
    showNotification(`Проект собран: ${result.tasks_created} задач`, 'success');
  } catch (err) {
    const detail = err.detail;
    const msg = detail?.message ?? detail ?? err.message ?? 'Ошибка сборки';
    const text = typeof msg === 'string' ? msg : 'Ошибка сборки';
    showProjectInspectorError(text);
    showNotification(text, 'error');
    console.error('Build gantt error:', err);
  } finally {
    hideSaving();
  }
}

async function saveInspectorProject(project) {
  const diff = getProjectInspectorDiff();
  if (!diff) return;
  showSaving();
  try {
    await api.patchProject(project.id, diff);
    // Refetch для согласованности (триггеров пересчёта на projects сейчас нет,
    // но ради единообразия с saveInspectorTask делаем read-after-write).
    _allProjects = await api.projects();
    const fresh = _allProjects.find(p => p.id === project.id);
    if (fresh) refreshInspectorProject(fresh);
    rerenderGantt();
    showNotification('Сохранено', 'success');
  } catch (err) {
    const detail = err.detail;
    const msg = detail?.message ?? detail ?? err.message ?? 'Ошибка сохранения';
    showNotification(typeof msg === 'string' ? msg : 'Ошибка сохранения', 'error');
    console.error('Project save error:', err);
  } finally {
    hideSaving();
  }
}

async function openActivateDialog(project) {
  const ok = window.confirm(
    `Активировать проект «${project.name}»?\n\n` +
    'Статус сменится с queued на active. Это **залочит baseline** для всех ' +
    'задач проекта (нельзя будет менять «Дни», «Минуты», «Бюджет», «Дедлайн ' +
    'вехи» без админ-разлока).\n\nПродолжить?'
  );
  if (!ok) return;
  await runProjectAction(project.id, () => api.activateProject(project.id), 'Проект активирован');
  // Re-fetch tasks тоже — baseline теперь залочен, колонки Баз. начало/окончание
  // должны заполниться.
  _allTasks = await api.tasks(_taskFetchParams);
  rerenderGantt();
}

async function openPauseDialog(project) {
  const v = await openModal({
    title: 'Поставить проект на паузу',
    message: project.name,
    fields: [{ name: 'reason', label: 'Причина (опционально)', type: 'text' }],
    confirmLabel: 'Поставить на паузу',
  });
  if (!v) return;
  const body = v.reason ? { reason: v.reason } : {};
  runProjectAction(project.id, () => api.pauseProject(project.id, body), 'Проект на паузе');
}

async function openProjectCompleteDialog(project) {
  const ok = window.confirm(
    `Завершить проект «${project.name}»?\n\n` +
    'Все задачи проекта должны быть закрыты. Статус сменится на done.\n\n' +
    'Продолжить?'
  );
  if (!ok) return;
  runProjectAction(
    project.id,
    () => api.completeProject(project.id, { done_criteria_met: true }),
    'Проект завершён',
  );
}

async function openProjectCancelDialog(project) {
  const v = await openModal({
    title: 'Отменить проект?',
    message:
      `Проект «${project.name}» будет помечен как cancelled и пропадёт из ` +
      `активных видов. Действие обратимо через @mentor_bot (UPDATE status). ` +
      `Дочерние задачи останутся как есть.`,
    fields: [{ name: 'reason', label: 'Причина отмены', type: 'text', required: true }],
    confirmLabel: 'Отменить проект',
    danger: true,
  });
  if (!v?.reason) return;
  runProjectAction(
    project.id,
    () => api.cancelProject(project.id, { reason: v.reason }),
    'Проект отменён',
    /* closeAfter */ true,
  );
}

async function runProjectAction(projectId, fn, successMsg, closeAfter = false) {
  showSaving();
  try {
    await fn();
    _allProjects = await api.projects();
    const fresh = _allProjects.find(p => p.id === projectId);
    if (fresh) {
      refreshInspectorProject(fresh);
    } else if (closeAfter || _selectedProjectId === projectId) {
      // Проект исчез из активного списка (например, cancel/complete отфильтрован
      // через `status != 'done' AND status != 'cancelled'`). Закрываем drawer.
      _selectedProjectId = null;
      closeProjectInspector();
    }
    rerenderGantt();
    showNotification(successMsg, 'success');
  } catch (err) {
    const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка';
    showNotification(typeof msg === 'string' ? msg : 'Ошибка', 'error');
    console.error('Project action error:', err);
  } finally {
    hideSaving();
  }
}

// ── Context menu actions (Phase D; modals come in Phase E) ──────────────────

function refreshTaskInGantt(updatedTask) {
  if (!updatedTask) return;
  // Sync _allTasks so the inspector and future re-renders see fresh values.
  const idx = _allTasks.findIndex(t => t.id === updatedTask.id);
  if (idx >= 0) _allTasks[idx] = { ..._allTasks[idx], ...updatedTask };

  if (gantt.isTaskExists(updatedTask.id)) {
    const t = gantt.getTask(updatedTask.id);
    t.text    = updatedTask.name;
    t._status = updatedTask.status;
    t._cc     = !!updatedTask.is_on_critical_chain;
    gantt.silent(() => gantt.updateTask(updatedTask.id));
  }
  refreshInspectorTask(updatedTask);
}

async function runTaskAction(taskId, fn, successMsg) {
  showSaving();
  try {
    const updated = await fn();
    if (updated && updated.id) refreshTaskInGantt(updated);
    showNotification(successMsg, 'success');
  } catch (err) {
    const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка';
    showNotification(typeof msg === 'string' ? msg : 'Ошибка', 'error');
    console.error('Task action error:', err);
  } finally {
    hideSaving();
  }
}

function buildTaskMenu(task) {
  const items = [];
  const status = task._status;
  const id     = task.id;
  const name   = task.text;

  if (status === 'todo') {
    items.push({
      label: '▶ Начать',
      action: () => runTaskAction(id, () => api.startTask(id), 'Задача начата'),
    });
  }
  // ✓ Завершить — единое поведение с инспектором (Фаза 1): доступно из любого
  // статуса кроме done/cancelled, не только из in_progress/blocked.
  if (status !== 'done' && status !== 'cancelled') {
    items.push({
      label: '✓ Завершить…',
      action: () => openCompleteDialog(id, name),
    });
  }
  if (status === 'in_progress') {
    items.push({
      label: '⛔ Заблокировать…',
      action: () => openBlockDialog(id, name),
    });
  }
  if (status === 'blocked') {
    items.push({
      label: '↺ Разблокировать',
      action: () => runTaskAction(id, () => api.unblockTask(id), 'Разблокировано'),
    });
  }
  items.push({
    label: '🗑 Удалить…',
    danger: true,
    action: () => openDeleteDialog(id, name),
  });
  return items;
}

async function openCompleteDialog(taskId, taskName) {
  // Предзаполнение факт. дат (ретроспективное закрытие). Используем datetime-local
  // (без TZ) — браузер показывает в локальном времени, бэкенд парсит как
  // local-naive datetime и сохраняет в TIMESTAMPTZ как есть. Если у задачи
  // есть started_at — берём его; иначе сейчас.
  const task = _allTasks.find(t => t.id === taskId);
  const toLocal = (d) => {
    // YYYY-MM-DDTHH:mm для <input type="datetime-local">
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const now = new Date();
  const startedAt = task?.started_at ? new Date(task.started_at) : now;

  const v = await openModal({
    title: 'Завершить задачу',
    message: taskName,
    fields: [
      {
        name: 'actual_minutes_spent',
        label: 'Сколько минут потрачено (опционально)',
        type: 'number',
        min: 0,
        placeholder: '0',
      },
      {
        name: 'actual_start_at',
        label: 'Факт. начало',
        type: 'datetime-local',
        value: toLocal(startedAt),
      },
      {
        name: 'actual_end_at',
        label: 'Факт. окончание',
        type: 'datetime-local',
        value: toLocal(now),
      },
    ],
    confirmLabel: 'Завершить',
  });
  if (!v) return;
  const body = {};
  if (Number.isFinite(v.actual_minutes_spent) && v.actual_minutes_spent >= 0) {
    body.actual_minutes_spent = Math.round(v.actual_minutes_spent);
  }
  if (v.actual_start_at) body.actual_start_at = v.actual_start_at;
  if (v.actual_end_at)   body.actual_end_at   = v.actual_end_at;
  runTaskAction(taskId, () => api.completeTask(taskId, body), 'Задача завершена');
}

async function openBlockDialog(taskId, taskName) {
  const v = await openModal({
    title: 'Заблокировать задачу',
    message: taskName,
    fields: [{ name: 'reason', label: 'Причина блокировки', type: 'text', required: true }],
    confirmLabel: 'Заблокировать',
  });
  if (!v?.reason) return;
  runTaskAction(taskId, () => api.blockTask(taskId, { reason: v.reason }), 'Заблокировано');
}

async function openDeleteDialog(taskId, taskName) {
  const isMs = !!_allTasks.find(t => t.id === taskId)?.is_milestone;
  const v = await openModal({
    title: isMs ? 'Удалить веху?' : 'Удалить задачу?',
    message: isMs
      ? `Это пометит веху «${taskName}» как cancelled. Дочерние задачи вернутся под проект (orphan-recovery), их данные не пропадут.`
      : `Это пометит задачу «${taskName}» как cancelled. Действие можно отменить через mentor-бот, но визуально она исчезнет с диаграммы.`,
    confirmLabel: 'Удалить',
    danger: true,
  });
  if (!v) return;
  showSaving();

  // Локально убрать задачу из view + кеша. Используем при успешном DELETE и
  // при «уже удалена» (404/409) — в обоих случаях итоговое состояние одно
  // и то же: задачи на сервере нет, и фронт не должен её показывать.
  const removeLocally = () => {
    if (_selectedTaskId === taskId) {
      _selectedTaskId = null;
      closeTaskInspector();
    }
    if (gantt.isTaskExists(taskId)) {
      gantt.deleteTask(taskId);
      gantt.render();
    }
    const idx = _allTasks.findIndex(t => t.id === taskId);
    if (idx >= 0) _allTasks.splice(idx, 1);
  };

  try {
    await api.deleteTask(taskId);
    removeLocally();
    showNotification('Задача удалена', 'success');
  } catch (err) {
    // 404 = бэкенд не нашёл; 409 = бэкенд считает уже cancelled. В обоих
    // случаях задачи в активном состоянии нет — пользователь хотел того
    // же результата, что фронт ему и показывает.
    if (err.status === 404 || err.status === 409) {
      removeLocally();
      showNotification('Задача уже была удалена (синхронизировано)', 'success');
    } else {
      const msg = err.detail?.message ?? err.detail ?? err.message ?? 'Ошибка удаления';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка удаления', 'error');
    }
  } finally {
    hideSaving();
  }
}

// ── Scale switcher ───────────────────────────────────────────────────────────

export function setGanttScale(scale) {
  // Roxber Teal: на «День» добавляем sub-scale с днями недели (Пн/Вт…)
  // и подсветкой weekend через CSS `.weekend`. На «Неделя» оставляем
  // как было — основная единица крупнее.
  const dowRu = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  const cfg = {
    hour:    { unit: 'hour',  step: 1,  fmt: '%H:00', sub: [{ unit: 'day',  step: 1, date: '%d %M %Y' }] },
    day:     {
      unit: 'day', step: 1, fmt: '%d %M',
      sub: [{
        unit: 'day', step: 1,
        template: (d) => dowRu[d.getDay()],
        css: (d) => ([0, 6].includes(d.getDay()) ? 'weekend-cell' : ''),
      }],
    },
    week:    {
      unit: 'week', step: 1, fmt: 'Нед %W',
      sub: [{
        unit: 'week', step: 1,
        // Subscale: диапазон дат недели (29.04 — 05.05)
        template: (d) => {
          const end = new Date(d);
          end.setDate(end.getDate() + 6);
          const dd = (x) => String(x.getDate()).padStart(2, '0');
          const mm = (x) => String(x.getMonth() + 1).padStart(2, '0');
          return `${dd(d)}.${mm(d)} — ${dd(end)}.${mm(end)}`;
        },
      }],
    },
    month:   { unit: 'month', step: 1,  fmt: '%F %Y', sub: [] },
    quarter: { unit: 'month', step: 3,  fmt: '%F %Y', sub: [] },
  };
  const c = cfg[scale] ?? cfg.week;
  gantt.config.scale_unit = c.unit;
  gantt.config.step       = c.step;
  gantt.config.date_scale = c.fmt;
  gantt.config.subscales  = c.sub;
  gantt.render();
}
